/* eslint-disable no-useless-escape */
import { isArray, isNil } from 'lodash'
import mixpanel from 'mixpanel-browser'
import moment from 'moment'

import { enTranslate, LEGACY_CODES, supportedLangs } from './translation'
import { getCurrencyAndRegion, getSubscriptionPlanId } from './payment'
import { convertToUSD } from './api'
import {
  CHECKOUT_ERRORS,
  EN_INTERFACE_PAYMENT_ERROR_MESSAGE,
} from './consts.mjs'
import { getLocationInfo, isCity } from './locations'
import { allAddonsTypes } from './addonTypes'
import { HOTJAR_EVENTS, trackHotjarEvent } from './tracking/hotjar'
import {
  getUserPlanNameOrTier,
  getUserPricingGroup,
  getUserRecurlyPlanName,
  getDaysSinceStartedTrial,
  getDaysSinceSignedUp,
  getStartTrialDate,
  getSignedUpDate,
  getUserParent,
  getUserStatus,
  getSignInMethod,
  getUserDevice,
} from './get-user-super-properties'

// initialization
const {
  REACT_APP_MIXPANEL_API_KEY,
  REACT_APP_IS_DEV,
  REACT_APP_MIXPANEL_PROXY_URL,
} = process.env
const isDevEnvironment = REACT_APP_IS_DEV === 'true'

if (REACT_APP_MIXPANEL_API_KEY) {
  mixpanel.init(REACT_APP_MIXPANEL_API_KEY, {
    debug: isDevEnvironment,
    ignore_dnt: isDevEnvironment,
    api_host: REACT_APP_MIXPANEL_PROXY_URL,
  })
}

let TIER_TO_PLAN_NAME_MAP

// all mixpanel properties
export const MIXPANEL_PROPS = {
  PURCHASE_BUTTON_LOCATION: {
    name: 'purchase_button_location',
    values: {
      NAVIGATION_SIDEBAR: 'navigation_sidebar',
      TOP_FREE_SEARCH_COUNT: 'top_free_search_count',
      BLOCK_OFF_DATA: 'block_off_data',
      EXPORT_MODAL: 'export_modal',
      LIMIT_REACHED_MODAL: 'limit_reached_modal',
      LIMIT_REACHED: 'limit_reached',
      UPGRADE_MODAL: 'upgrade_modal',
      ACCOUNT_BILLING: 'account_billing',
      PROJECT_SETTINGS_MODAL: 'project_settings_modal',
      PROJECT_CREATION_FLOW: 'project_creation_flow',
      UPGRADE_AIW_MODAL: 'upgrade_aiw_modal',
      ADD_KEYWORD_TO_LIST_MODAL: 'add_to_list_modal',
      ACCOUNT_BILLING_ADDON: 'account_billing_addon',
      ACCOUNT_BILLING_AIW_ADDON: 'account_billing_aiw_addon',
      FILTERS: 'filters',
      LOAD_MORE: 'load_more',
      AIW_WORD_COUNT_USAGE_CARD: 'aiw_word_count_usage_card',
      UPDATE_METRICS_MODAL: 'update_metrics_modal',
      PRICE_PAGE: 'price_page',
      SEO_UBS_BANNER: 'seo_ubs_banner',
      SEO_PRICING_PAGE_BANNER: 'seo_pricing_page_banner',
      EXPORT_PDF_IMG_MODAL: 'export_pdf_img_modal',
      BF_AIW_BANNER: 'BF2024 aiw in app banner',
      BF_EXT_BANNER: 'BF2024 chrome extension',
      BF_UBS_BANNER: 'BF2024 in app banner',
      BF_PRICING_PAGE_BANNER: 'BF2024 pricing page banner',
      HOLIDAY_AIW_BANNER: 'holiday2024 aiw in app banner',
      HOLIDAY_EXT_BANNER: 'holiday2024 chrome extension',
      HOLIDAY_UBS_BANNER: 'holiday2024 ubs in app banner',
      HOLIDAY_ALERT_CTA: 'holiday2024 alert cta',
      KEYWORD_VISUALIZATION_DATA: 'keyword_visualization data',
      ADD_LOCATION_MODAL: 'add_location_modal',
      ADD_COMPETITORS_MODAL: 'add_competitors_modal',
      BULK_ANALYSIS: 'keyword_bulk_analysis_modal',
    },
  },
  BUTTON_CTA: {
    name: 'button_cta',
    values: {
      UPGRADE: 'upgrade',
      START_TRIAL: 'start_trial',
      BUY_ADDON: 'buy_addon',
      SIGN_IN: 'sign_in',
    },
  },
  PAGE_NAME: {
    name: 'page_name',
    values: {
      DASHBOARD: 'dashboard',
      RANK_TRACKING: 'rank_tracking',
      SEO_OPPORTUNITIES: 'seo_opportunities',
      MY_WORKSPACE: 'my_workspace',
      CHROME_EXTENSION: 'chrome_extension',
      SITE_AUDIT: 'site_audit',
      KEYWORD_OVERVIEW: 'keyword_overview',
      KEYWORD_IDEAS: 'keyword_ideas',
      KEYWORD_VISUALIZATION: 'keyword_visualization',
      KEYWORD_BULK_ANALYSIS: 'keyword_bulk_analysis',
      KEYWORDS_BY_TRAFFIC: 'keywords_by_traffic',
      SIMILAR_WEBSITES: 'similar_websites',
      CONTENT_IDEAS: 'content_ideas',
      KEYWORD_LISTS: 'keyword_lists',
      TRAFFIC_OVERVIEW: 'traffic_overview',
      TOP_PAGES_BY_TRAFFIC: 'top_pages_by_traffic',
      BACKLINKS_OVERVIEW: 'backlinks_overview',
      BACKLINKS_OPPORTUNITY: 'backlinks_opportunity',
      LABS: 'labs',
      KEYWORD_GENERATOR: 'keyword_generator',
      ACCOUNT_AND_BILLING: 'account_and_billing',
      MANAGE_USERS: 'manage_users',
      MANAGE_ADDONS: 'manage_addons',
      MANAGE_NOTIFICATIONS: 'manage_notifications',
      ALERTS: 'alerts_page',
      PLANS_AND_PRICING: 'pricing_page',
      LOG_IN: 'sign_in_page',
      REGISTER: 'sign_up_page',
      CHECKOUT: 'checkout',
      AIW: 'AIW',
      COMPETITOR_TRACKING: 'competitor_tracking',
      ONBOARDING_PAGE: 'onboarding_page',
    },
    defaultValue: () => {
      const { values } = MIXPANEL_PROPS.PAGE_NAME
      const { pathname } = new URL(window.location.href)
      let [path] = pathname.split('?')
      if (path.startsWith('/')) {
        path = path.substring(1)
      }

      const [firstPath] = path.split('/')

      if (
        supportedLangs.includes(firstPath) ||
        LEGACY_CODES.includes(firstPath)
      ) {
        path = path.substring(3)
      }
      if (path.startsWith('dashboard/competitor_tracking')) {
        return values.COMPETITOR_TRACKING
      }
      if (path.startsWith('dashboard') || path === '') {
        return values.DASHBOARD
      }
      if (path.startsWith('position_tracking')) {
        return values.RANK_TRACKING
      }
      if (path.startsWith('seo_opportunities')) {
        return values.SEO_OPPORTUNITIES
      }
      if (path.startsWith('workspace')) {
        return values.MY_WORKSPACE
      }
      if (path.startsWith('extension')) {
        return values.CHROME_EXTENSION
      }
      if (path.startsWith('seo_analyzer/site_audit')) {
        return values.SITE_AUDIT
      }
      if (path.startsWith('ubersuggest/overview')) {
        return values.KEYWORD_OVERVIEW
      }
      if (path.startsWith('ubersuggest/keyword_bulk_analysis')) {
        return values.KEYWORD_BULK_ANALYSIS
      }
      if (path.startsWith('ubersuggest/keyword_ideas')) {
        return values.KEYWORD_IDEAS
      }
      if (path.startsWith('ubersuggest/keyword_visualization')) {
        return values.KEYWORD_VISUALIZATION
      }
      if (path.startsWith('traffic_analyzer/keywords')) {
        return values.KEYWORDS_BY_TRAFFIC
      }
      if (path.startsWith('traffic_analyzer/competitors')) {
        return values.SIMILAR_WEBSITES
      }
      if (path.startsWith('ubersuggest/content_ideas')) {
        return values.CONTENT_IDEAS
      }
      if (path.startsWith('ubersuggest/keyword_lists')) {
        return values.KEYWORD_LISTS
      }
      if (path.startsWith('traffic_analyzer/overview')) {
        return values.TRAFFIC_OVERVIEW
      }
      if (path.startsWith('traffic_analyzer/top_pages')) {
        return values.TOP_PAGES_BY_TRAFFIC
      }
      if (path.startsWith('seo_analyzer/backlinks')) {
        return values.BACKLINKS_OVERVIEW
      }
      if (path.startsWith('seo_analyzer/backlink_opportunity')) {
        return values.BACKLINKS_OPPORTUNITY
      }
      if (path.startsWith('labs/landing')) {
        return values.LABS
      }
      if (path.startsWith('labs/keywords_generator')) {
        return values.KEYWORD_GENERATOR
      }
      if (path.startsWith('settings/account_billing')) {
        return values.ACCOUNT_AND_BILLING
      }
      if (path.startsWith('settings/manage_users')) {
        return values.MANAGE_USERS
      }
      if (path.startsWith('settings/manage_addons')) {
        return values.MANAGE_ADDONS
      }
      if (path.startsWith('settings/notifications')) {
        return values.MANAGE_NOTIFICATIONS
      }
      if (path.startsWith('alerts')) {
        return values.ALERTS
      }
      if (path.startsWith('pricing')) {
        return values.PLANS_AND_PRICING
      }
      if (path.startsWith('login')) {
        return values.LOG_IN
      }
      if (path.startsWith('register')) {
        return values.REGISTER
      }
      if (path.startsWith('checkout')) {
        return values.CHECKOUT
      }

      return path
    },
  },
  USER_STATUS: {
    name: 'user_status',
    values: {
      FREE: 'free',
      PAID: 'paid',
      TRIAL: 'trial',
      UNLOGGED: 'unlogged',
    },
    defaultValue: () => {
      const rawUser = localStorage.getItem('user')
      const { values } = MIXPANEL_PROPS.USER_STATUS
      let userStatus = rawUser ? values.FREE : values.UNLOGGED
      let user

      try {
        user = JSON.parse(rawUser)
      } catch (e) {
        userStatus = values.UNLOGGED
      }

      if (user?.subscription) {
        if (
          user.subscription.subscriptionStatus === 'active' ||
          user.subscription.subscriptionStatus === 'paid'
        ) {
          userStatus = values.PAID
        } else if (user.subscription.subscriptionStatus === 'trialing') {
          userStatus = values.TRIAL
        }
      }

      return userStatus
    },
  },
  get AIW_USER_STATUS() {
    return {
      ...this.USER_STATUS,
      name: 'aiw user status',
    }
  },
  USER_CURRENT_PLAN: {
    name: 'user_current_plan',
    defaultValue: () => {
      try {
        const user = JSON.parse(localStorage.getItem('user'))
        if (user?.subscription) {
          const { tier, planInterval } = user.subscription
          if (tier && planInterval) {
            return getSubscriptionPlanId(user.countryCode, tier, planInterval)
          }
        } else if (user?.tier === 'free') {
          return 'free'
        }
      } catch (e) {
        return ''
      }
      return ''
    },
  },
  AIW_ADDON_TYPE: {
    name: 'aiw addon type',
    values: {
      ADDON_AIW_BLOGGER: allAddonsTypes.ADDON_AIW_BLOGGER,
      ADDON_AIW_BUSINESS: allAddonsTypes.ADDON_AIW_BUSINESS,
      ADDON_AIW_SHAKESPEARE: allAddonsTypes.ADDON_AIW_SHAKESPEARE,
    },
  },
  USER_BACKEND_ID: {
    name: 'user_backend_ID',
  },
  SIGN_IN_METHOD: {
    name: 'sign_in_method',
    values: {
      GOOGLE: 'google',
      EMAIL: 'email',
    },
    defaultValue: () => {
      try {
        const { values } = MIXPANEL_PROPS.SIGN_IN_METHOD
        const user = JSON.parse(localStorage.getItem('user'))
        if (user?.user_type) {
          if (user.user_type === 'google') {
            return values.GOOGLE
          }
          return values.EMAIL
        }
      } catch (e) {
        return ''
      }
      return ''
    },
  },
  PLAN_NAME: {
    name: 'plan_name',
    values: {
      INDIVIDUAL: 'individual',
      BUSINESS: 'business',
      ENTERPRISE: 'enterprise',
      ZERO: 'zero',
      RESTART: 'restart',
    },
    defaultValue: () => {
      try {
        const subscription =
          JSON.parse(localStorage.getItem('subscription')) ||
          JSON.parse(localStorage.getItem('user')).subscription
        if (subscription) {
          const { tier } = subscription
          if (tier) {
            return TIER_TO_PLAN_NAME_MAP[tier] || ''
          }
        }
      } catch (e) {
        return ''
      }
      return ''
    },
  },
  PLAN_TIER: {
    name: 'plan_tier',
    values: {
      TIER1: 'tier1',
      TIER2: 'tier2',
      TIER3: 'tier3',
      ZERO: 'zero',
      RESTART: 'restart',
    },
    defaultValue: () => {
      try {
        const subscription =
          JSON.parse(localStorage.getItem('subscription')) ||
          JSON.parse(localStorage.getItem('user')).subscription
        return subscription?.tier || ''
      } catch (e) {
        return ''
      }
    },
  },
  BILLING_CYCLE: {
    name: 'billing_cycle',
    values: {
      MONTHLY: 'monthly',
      YEARLY: 'yearly',
      LIFETIME: 'lifetime',
    },
    defaultValue: () => {
      try {
        const subscription =
          JSON.parse(localStorage.getItem('subscription')) ||
          JSON.parse(localStorage.getItem('user')).subscription
        if (subscription) {
          return subscription.planInterval
        }
      } catch (e) {
        return ''
      }
      return ''
    },
  },
  PRICING_PAGE_BUTTON_LOCATION: {
    name: 'pricing_page_button_location',
    values: {
      TOP_PLAN_CARDS: 'top_plan_cards',
      PLANS_COMPARISON_TABLE: 'plans_comparison_table',
      BOTTOM_FAQ: 'bottom_page',
      MOBILE_PLAN_CARDS: 'mobile_plan_cards',
    },
  },
  PAYMENT_METHOD: {
    name: 'payment_method',
    values: {
      CREDIT_CARD: 'credit_card',
      PAYPAL: 'paypal',
    },
    defaultValue: () => {
      try {
        const { values } = MIXPANEL_PROPS.PAYMENT_METHOD
        const user = JSON.parse(localStorage.getItem('user'))
        if (user?.payment_method?.object) {
          const paymentMethod = user.payment_method.object
          return paymentMethod === 'paypal' ? values.PAYPAL : values.CREDIT_CARD
        }
      } catch (e) {
        return ''
      }
      return ''
    },
  },
  PAYMENT_STATUS: {
    name: 'payment_status',
    values: {
      ERROR: 'error',
      PAYMENT_ISSUE: 'payment_issue',
      SUCCESS: 'success',
    },
  },
  PAYMENT_DATE: {
    name: 'payment_date',
  },
  PAYMENT_ERROR_MESSAGE: {
    name: 'payment_error_message',
  },
  RECURLY_PAYMENT_ERROR_MESSAGE: {
    name: 'recurly_payment_error_message',
  },
  EN_INTERFACE_PAYMENT_ERROR_MESSAGE: {
    name: 'en_interface_payment_error_message',
  },
  PAYMENT_ERROR_PARAM: {
    name: 'payment_error_param',
  },
  PAYMENT_ISSUE_DESCRIPTION: {
    name: 'payment_issue_description',
  },
  USER_PRICING_GROUP: {
    name: 'user_pricing_group',
    values: {
      GLOBAL: 'global',
      REGULAR: 'regular',
    },
  },
  ADD_PROJECT_BUTTON_LOCATION: {
    name: 'add_project_button_location',
    values: {
      DASHBOARD_TOP: 'dashboard_top',
      SIDEBAR_TOP: 'add_first_project_navigation_sidebar',
      SIDEBAR_PROJECT_SELECTOR: 'sidebar_project_selector',
      ADD_FIRST_PROJECT_MAIN_PAGE: 'add_first_project_main_page',
      RANK_TRACKING_PROJECT_SELECTOR: 'rank_tracking_project_selector',
      DASHBOARD_PROJECT_SELECTOR: 'dashboard_project_selector',
      ADD_TO_LIST: 'add_to_list',
      LANDING_NEW_PURCHASE: 'landing_new_purchase',
      LANDING_NEW_ACCOUNT: 'landing_new_account',
      NEW_CHECKOUT_SUCCESS_MODAL: 'new_checkout_success_modal',
      PROJECT_ADDON: 'project_addon',
      ONBOARDING_PAGE: 'onboarding_page',
    },
  },
  ONBOARDING_SUCCESS_PATH: {
    name: 'onboarding_success_path',
    values: {
      DO_MY_OWN: 'do_my_own',
      HIRE_SERVICE_STRATEGIC: 'hire_strategic_services',
      HIRE_SERVICE_FULL: 'request_full_service_quote',
    },
  },
  ADDED_PROJECT_LANGUAGES: {
    name: 'added_project_languages',
  },
  PROJECT_LANGUAGES_COUNT: {
    name: 'project_languages_count',
  },
  ADDED_PROJECT_LOCATIONS: {
    name: 'added_project_locations',
  },
  PROJECT_LOCATIONS_COUNT: {
    name: 'project_locations_count',
  },
  PROJECT_KEYWORDS_COUNT: {
    name: 'project_keywords_count',
  },
  SKIPPED_ADD_KEYWORDS: {
    name: 'skipped_add_keywords',
  },
  PROJECT_COMPETITORS_COUNT: {
    name: 'project_competitors_count',
  },
  SKIPPED_ADD_COMPETITORS: {
    name: 'skipped_add_competitors',
  },
  PROJECT_AGENCY_OFFER_RESPONSE: {
    name: 'project_agency_offer_response',
  },
  SELECTED_LOCATION: {
    name: 'selected_location',
  },
  SELECTED_LANGUAGE: {
    name: 'selected_language',
  },
  SEARCH_RESULTS_COUNT: {
    name: 'search_results_count',
  },
  SEARCH_INPUT: {
    name: 'search_input',
    values: {
      DOMAIN: 'domain',
      KEYWORD: 'keyword',
      URL: 'url',
    },
  },
  SEARCH_KEYWORDS_INPUT_COUNT: {
    name: 'search_keywords_input_count',
  },
  SEARCH_INPUT_LENGTH: {
    name: 'search_input_length',
  },
  SEARCH_INPUT_TERM: {
    name: 'search_input_term',
  },
  EXPORT_TYPE: {
    name: 'export_type',
    values: {
      CSV: 'csv',
      PDF: 'pdf',
      IMG: 'img',
    },
  },
  EXPORTED_CHART: {
    name: 'exported_chart',
    values: {
      SEARCH_VOLUME: 'search_volume',
      ORGANIC_TRAFFIC: 'organic_traffic',
      DOMAIN_AUTHORITY: 'domain_authority',
      BACKLINKS: 'backlinks',
      SIMILAR_WEBSITES: 'similar_websites',
      BACKLINKS_OVER_TIME: 'backlinks_over_time',
      REFERRING_DOMAINS_BY_DA: 'referring_domains_by_da',
      NEW_LOST_REFERRING_DOMAINS: 'new_lost_referring_domains',
      SEO_KEYWORDS_RANKING: 'seo_keywords_ranking',
      ESTIMATED_ORGANIC_TRAFFIC: 'estimated_organic_traffic',
      ESTIMATED_TRAFFIC_VALUE: 'estimated_traffic_value',
      DASHBOARD_BACKLINKS: 'dashboard_backlinks',
      ON_PAGE_SEO_SCORE: 'on_page_seo_score',
      SITE_AUDIT: 'site_audit',
    },
  },
  SELECTED_LOCATION_BREAKDOWN: {
    name: 'selected_location_breakdown',
    values: {
      FIRST: '1st',
      SECOND: '2nd',
      THIRD: '3rd',
      FOURTH: '4th',
      FIFTH: '5th',
      ALL: 'all_locations',
    },
  },
  METRIC_SELECTED: {
    name: 'metric_selected',
    values: {
      SEO_DIFFICULT: 'seo_difficult',
      SEARCH_VOLUME: 'search_volume',
      PAID_DIFFICULT: 'paid_difficult',
      COST_PER_CLICK: 'cost_per_click',
      PAID_KEYWORDS: 'paid_keywords',
      ORGANIC_KEYWORDS: 'organic_keywords',
      DOMAIN_AUTHORITY: 'domain_authority',
      BACKLINKS: 'backlinks',
      REFERRING_DOMAINS: 'referring_domains',
    },
  },
  WIDGET_SELECTED: {
    name: 'widget_selected',
    values: {
      KEYWORD_IDEAS: 'keyword_ideas',
      CONTENT_IDEAS: 'content_ideas',
      TOP_SEO_PAGES: 'top_seo_pages',
      SEO_KEYWORDS: 'seo_keywords',
      BACKLINKS: 'backlinks',
      BACKLINK: 'backlink',
      REFERRING_DOMAIN: 'referring_domain',
      TOP_BACKLINK: 'top_backlink',
      GOOGLE_SEARCH_RESULTS: 'google_search_results',
      KEYWORDS_BY_TRAFFIC: 'keywords_by_traffic',
      TRACKED_KEYWORDS: 'tracked_keywords',
      TRACKED_COMPETITORS: 'tracked_competitors',
      NEW_REFERRAL_DOMAINS: 'new_referral_domains',
      LOST_REFERRAL_DOMAINS: 'lost_referral_domains',
      SEO_ISSUE: 'seo_issue',
      PAGE_URL: 'page_url',
      SIMILAR_WEBSITES: 'similar_websites',
      TOP_PAGES_BY_TRAFFIC: 'top_pages_by_traffic',
      TOP_PAGES_BY_COUNTRY: 'top_pages_by_country',
      KEYWORD_LIST: 'keyword_list',
      KW_IDEAS_SEARCH_BY_KEYWORD: 'kw_ideas_search_by_keyword',
      KW_IDEAS_SEARCH_BY_WEBSITE: 'kw_ideas_search_by_website',
      COMMON_KEYWORDS: 'common_keywords',
      KEYWORDS_GAP: 'keywords_gap',
      TOP_SEO_OPPORTUNITIES: 'top_seo_opportunities',
      KW_BULK_ANALYSIS: 'kw_bulk_analysis',
    },
  },
  ROW_POSITION: {
    name: 'row_position',
  },
  DISPLAYED_METRIC: {
    name: 'displayed_metric',
    values: {
      GOOGLE_SEARCH_RESULTS: 'google_search_results',
      BACKLINKS: 'backlinks',
      KEYWORDS: 'keywords',
      TREND: 'trend',
      PAGE_URL: 'page_url',
      TOP_BACKLINK: 'top_backlink',
      COMMON_KEYWORDS: 'common_keywords',
      KEYWORDS_GAP: 'keywords_gap',
      TOP_PAGES_BY_COUNTRY: 'top_pages_by_country',
    },
  },
  SERP_FEATURES: {
    name: 'serp_features',
  },
  TOGGLE_STATUS: {
    name: 'toggle_status',
    values: {
      SHOW: 'show',
      HIDE: 'hide',
    },
  },
  CONTAIN_RESULTS: {
    name: 'contain_results',
    values: {
      TRUE: 'true',
      FALSE: 'false',
    },
  },
  APP_SECTION: {
    name: 'app_section',
    values: {
      GENERAL: 'general',
      SITE_AUDIT: 'site_audit',
      KEYWORD_RESEARCH: 'keyword_research',
      TRAFFIC_ESTIMATION: 'traffic_estimation',
      BACKLINKS: 'backlinks',
      LABS: 'labs',
    },
    defaultValue: () => {
      const { values } = MIXPANEL_PROPS.APP_SECTION
      const { pathname } = new URL(window.location.href)
      let [path] = pathname.split('?')
      if (path.startsWith('/')) {
        path = path.substring(1)
      }
      const [firstPath] = path.split('/')

      if (
        supportedLangs.includes(firstPath) ||
        LEGACY_CODES.includes(firstPath)
      ) {
        path = path.substring(3)
      }
      if (
        !path.length ||
        path.startsWith('dashboard') ||
        path.startsWith('position_tracking') ||
        path.startsWith('seo_opportunities') ||
        path.startsWith('extension') ||
        path.startsWith('workspace')
      ) {
        return values.GENERAL
      }
      if (
        path.startsWith('ubersuggest/overview') ||
        path.startsWith('ubersuggest/keyword_ideas') ||
        path.startsWith('ubersuggest/keyword_visualization') ||
        path.startsWith('traffic_analyzer/keywords') ||
        path.startsWith('traffic_analyzer/competitors') ||
        path.startsWith('ubersuggest/content_ideas') ||
        path.startsWith('ubersuggest/keyword_lists') ||
        path.startsWith('ubersuggest/keyword_bulk_analysis')
      ) {
        return values.KEYWORD_RESEARCH
      }
      if (
        path.startsWith('traffic_analyzer/overview') ||
        path.startsWith('traffic_analyzer/top_pages')
      ) {
        return values.TRAFFIC_ESTIMATION
      }
      if (
        path.startsWith('seo_analyzer/backlinks') ||
        path.startsWith('seo_analyzer/backlink_opportunity')
      ) {
        return values.BACKLINKS
      }
      if (path.startsWith('labs/keywords_generator')) {
        return values.LABS
      }
      if (path.startsWith('labs/landing')) {
        return values.LABS
      }
      if (path.startsWith('seo_analyzer/site_audit')) {
        return values.SITE_AUDIT
      }

      return ''
    },
  },
  AUDIT_COUNT_PAGES_CRAWLED: {
    name: 'audit_count_pages_crawled',
  },
  ADDON_NAME: {
    name: 'add_on_name',
  },
  USER_HAS_ADD_ONS_ACTIVE: {
    name: 'user_has_add_ons_active',
  },
  ADDON_COUNT: {
    name: 'add_on_count',
  },
  ADDON_ON_LIST: {
    name: 'add_on_list',
  },
  USD_VALUE: {
    name: 'usd_value',
  },
  PROMOTION: {
    name: 'promotion',
    values: {
      LIFETIME_5: 'lifetime_5',
    },
  },
  SESSION_NUMBER: {
    name: 'session_number',
  },
  INDUSTRY: {
    name: 'industry',
  },
  BUSINESS_TYPE: {
    name: 'business_type',
  },
  CHALLENGES: {
    name: 'challenges',
  },
  NUMBER_OF_EMPLOYEES: {
    name: 'number_of_employees',
  },
  URGENCY_FOR_HELP: {
    name: 'urgency_for_help',
  },
  MONTHLY_MARKETING_BUDGET: {
    name: 'monthly_marketing_budget',
  },
  PHONE: {
    name: 'phone',
  },
  EMAIL: {
    name: 'email',
  },
  FIRSTNAME: {
    name: 'firstname',
  },
  LASTNAME: {
    name: 'lastname',
  },
  COUNTRY: {
    name: 'country',
  },
  DISMISSED_MODAL: {
    name: 'dismissed modal',
    values: {
      MODAL_CLOSE: 'modal close',
      MODAL_REMIND_LATER: 'modal remind later',
    },
  },
  SOURCE: {
    name: 'source',
    values: {
      NEW_CHECKOUT_MODAL: 'new_checkout_modal',
      BILLING_PAGE: 'billing_page',
      ASK_FOR_SIGNUP_MODAL: 'ask_for_signup_modal',
      PRICING_PAGE_CHECKOUT_MODAL: 'pricing_page_checkout_modal',
      PRICING_PAGE: 'pricing_page',
      AIW_PRICING_PAGE: 'aiw_pricing_page',
    },
  },
  CANCELLATION_FEEDBACK: {
    name: 'cancellation_feedback',
  },
  CANCELLATION_REASON: {
    name: 'cancellation_reason',
    values: {
      REASON_COST: 'reason_cost',
      REASON_DIFFICULTY_OF_USE: 'reason_difficulty_of_use',
      REASON_MISSING_FUNCTIONALITY: 'reason_missing_functionality',
      REASON_USING_OTHER_PRODUCT: 'reason_using_other_product',
      REASON_TEMPORARY_CANCELLATION: 'reason_temporary_cancellation',
      REASON_DATA_ISSUE: 'reason_data_issue',
      REASON_NOT_WHAT_I_WAS_EXPECTING: 'reason_not_what_i_was_expecting',
    },
  },
  FEEDBACK: {
    name: 'feedback',
  },
  TREND_SELECTED: {
    name: 'trend_selected',
    values: {
      ORGANIC_TRAFFIC: 'organic_traffic',
      DOMAIN_AUTHORITY: 'domain_authority',
      BACKLINKS: 'backlinks',
    },
  },
  TAB_SELECTED: {
    name: 'tab_selected',
    values: {
      SUGGESTIONS: 'suggestions',
      RELATED: 'related',
      QUESTIONS: 'questions',
      PREPOSITIONS: 'prepositions',
      COMPARISONS: 'comparisons',
    },
  },
  KEYWORD_IN_LIST_COUNT: {
    name: 'keywords_in_list_count',
  },
  ADDED_KEYWORDS_COUNT: {
    name: 'added_keywords_count',
  },
  ADDED_LISTS_COUNT: {
    name: 'added_lists_count',
  },
  ADDED_KEYWORDS_TYPE: {
    name: 'added_keywords_type',
    values: {
      RANK_TRACKING: 'rank_tracking',
      KEYWORD_LIST: 'keyword_list',
    },
  },
  CONNECT_WITH_GOOGLE_BUTTON_LOCATION: {
    name: 'button_location',
    values: {
      PERIOD_FILTER_BAR: 'period_filter_bar',
      ALERT_TEXT: 'alert_text',
      CHART: 'chart',
    },
  },
  GOOGLE_TOOL_CONNECTED: {
    name: 'google_tool_connected',
    values: {
      GA: 'GA',
      GSC: 'GSC',
      GA_GSC: 'GA_GSC',
    },
  },
  UPDATE_TYPE: {
    name: 'update_type',
    values: {
      UPDATE_ALL: 'update_all',
      UPDATE_SELECTED: 'update_selected',
      UPDATE_REMAINING_CREDITS: 'update_remaining_credits',
      UPDATE_ONE_BY_ONE: 'update_one_by_one',
    },
  },
  UPDATE_METRIC: {
    name: 'update_metric',
    values: {
      SEARCH_INTENT: 'search_intent',
      SEO_DIFFICULTY: 'seo_difficulty',
    },
  },
  UPDATE_KEYWORDS_COUNT: {
    name: 'update_keywords_count',
  },
  MENU_SUBITEM_NAME: {
    name: 'menu_subitem_name',
    values: {
      DATA_ANALYTICS: 'data_analytics',
      EARNED_MEDIA: 'earned_media',
      PAID_MEDIA: 'paid_media',
      CREATIVE: 'creative',
      TECHNOLOGY: 'technology',
    },
  },
  BUTTON_LOCATION: {
    name: 'button_location',
  },
  DAYS_SINCE_SIGNED_UP: {
    name: 'days_since_signed_up',
    defaultValue: () => {
      try {
        const user = JSON.parse(localStorage.getItem('user'))

        if (!user) {
          return ''
        }

        return moment.utc().diff(moment.utc(user.created_at * 1000), 'days')
      } catch (e) {
        return ''
      }
    },
  },
  DAYS_SINCE_STARTED_TRIAL: {
    name: 'days_since_started_trial',
  },
  START_TRIAL_DATE: {
    name: 'start_trial_date',
  },
  SIGNED_UP_DATE: {
    name: 'signed_up_date',
  },
  DAYS_SINCE_SUBSCRIBED: {
    name: 'days_since_subscribed',
    defaultValue: () => {
      try {
        const user = JSON.parse(localStorage.getItem('user'))

        if (!user || !user.payment_account_created_at) {
          return ''
        }

        return moment
          .utc()
          .diff(moment.utc(user.payment_account_created_at * 1000), 'days')
      } catch (e) {
        return ''
      }
    },
  },
  EXPERIMENT_LANDING_PAGE_DASHBOARD: {
    name: 'experiment_landing_page_dashboard',
    values: {
      CONTROL: false,
      VARIANT: true,
    },
    defaultValue: () => null,
  },
  LANDING_PAGE_DASHBOARD_BUTTON_CLICKED: {
    name: 'dashboard_button_clicked',
    values: {
      ADD_WEBSITE_HEADER: 'Add Website (header)',
      ADD_WEBSITE_SEO: 'Add Website (SEO)',
      ADD_WEBSITE_BACKLINK: 'Add Website (Backlink)',
      ADD_WEBSITE_KEYWORD_RANK: 'Add Website (Keyword rank)',
      INSTALL_CHROME_EXTENSION: 'Google Chrome Extension',
      ADD_WEBSITE_FOOTER: 'Add Website (footer)',
      ADD_KEYWORDS_HEADER: 'Add Keywords (header)',
      ADD_COMPETITORS_HEADER: 'Add Competitors (header)',
    },
  },
  USER_PARENT: {
    name: 'user_parent',
    values: {
      INVITED: 'invited',
      PARENT: 'parent',
      EMPTY: '',
    },
    defaultValue: () => {
      try {
        const user = JSON.parse(localStorage.getItem('user'))
        if (
          Object.values(MIXPANEL_PROPS.USER_PARENT.values).includes(
            user?.multi_user_type,
          )
        ) {
          return user.multi_user_type
        }
      } catch (e) {
        return MIXPANEL_PROPS.USER_PARENT.values.EMPTY
      }
      return MIXPANEL_PROPS.USER_PARENT.values.EMPTY
    },
  },
  HAS_PROJECT: {
    name: 'has_project',
    values: {
      TRUE: 'true',
      FALSE: 'false',
    },
  },
  HAS_PROJECT_WITH_TRACKED_KEYWORDS: {
    name: 'has_project_with_tracked_keywords',
    values: {
      TRUE: 'true',
      FALSE: 'false',
    },
  },
  OPPORTUNITY_TYPE: {
    name: 'opportunity_type',
    values: {
      OPTIMIZE: 'optimize',
      SEO_ISSUE: 'seo_issue',
      NEW_CONTENT: 'new_content',
    },
    getValue: ({ isSeoIssue, isNewContent }) => {
      const { SEO_ISSUE, NEW_CONTENT, OPTIMIZE } =
        MIXPANEL_PROPS.OPPORTUNITY_TYPE.values

      if (isSeoIssue) {
        return SEO_ISSUE
      }
      return isNewContent ? NEW_CONTENT : OPTIMIZE
    },
  },
  PREFERENCE: {
    name: 'preference',
    values: {
      THUMBS_UP: 'thumbs_up',
      THUMBS_DOWN: 'thumbs_down',
    },
  },
  ONBOARDING_QUESTIONNAIRE_NAME: {
    name: 'questionnaire_name',
    values: {
      ONBOARDING: 'Onboarding',
    },
  },
  ONBOARDING_QUESTIONNAIRE_COMPLETED_LOCATION: {
    name: 'questionnaire_completed_location',
    values: {
      MODAL: 'modal',
      NOTIFICATIONS: 'notifications',
    },
  },
  FILTERED_METRIC: {
    name: 'filtered_metric',
    values: {
      SD: 'seo_difficulty',
      VOLUME: 'volume',
      CPC: 'cpc',
      RANKKEYWORD: 'page_1_ranking_potencial',
      ALL_LINK: 'all_links',
      FOLLOW: 'follow',
      NO_FOLLOW: 'no_follow',
      ONE_LINK_PER_DOMAIN: 'one_link_per_domain',
      SEO_OPPORTUNITIES: 'seo_opportunities',
      REFDOMAINS: 'ref_domains',
      BACKLINKS: 'back_links',
      INCLUDE_KEYWORD: 'include_keyword',
      EXCLUDE_KEYWORD: 'exclude_keyword',
      INCLUDE_AND_EXCLUDE_KEYWORD: 'include_and_exclude_keyword',
    },
    getValue: ({ includeSize, excludeSize }) => {
      if (includeSize && !excludeSize) {
        return MIXPANEL_PROPS.FILTERED_METRIC.values.INCLUDE_KEYWORD
      }
      if (!includeSize && excludeSize) {
        return MIXPANEL_PROPS.FILTERED_METRIC.values.EXCLUDE_KEYWORD
      }
      return MIXPANEL_PROPS.FILTERED_METRIC.values.INCLUDE_AND_EXCLUDE_KEYWORD
    },
  },
  FILTERED_VALUE: {
    name: 'filtered_value',
    values: {
      OPTIMIZE: 'optimize',
      NEW_CONTENT: 'new_content',
      SEO_ISSUE: 'seo_issue',
    },
    getValue: ({ minValue, maxValue, keywordList = [] }) => {
      if (keywordList.length > 0) {
        return keywordList.join(', ')
      }
      return `${minValue} - ${maxValue}`
    },
  },
  SORTED_COLUMN: {
    name: 'sorted_column',
    values: {
      SD: 'seo_difficulty',
      AVG_SD: 'avg_seo_difficulty',
      PD: 'avg_pd',
      AVG_PD: 'avg_pd',
      CPC: 'cpc',
      AVG_CPC: 'avg_cpc',
      VOLUME: 'volume',
      TOTAL_VOLUME: 'total_volume',
      PV: 'pv',
      KEYWORD: 'keyword',
      KEYWORD_COUNT: 'keyword_count',
      NAME: 'name',
      POSITION: 'position',
      CLICKS: 'clicks',
      PAGE_AUTHORITY: 'page_authority',
      DOMAIN_AUTHORITY: 'domain_authority',
      PAGE: 'page',
    },
  },
  SORTED_TYPE: {
    name: 'sorted_type',
    values: {
      ASCENDING: 'ascending',
      DESCENDING: 'descending',
    },
  },
  PAGINATION_TYPE: {
    name: 'pagination_type',
    values: {
      FIRST: 'first',
      LAST: 'last',
      NEXT: 'next',
      PREVIOUS: 'previous',
    },
  },
  BUTTON_NAME: {
    name: 'button_name',
    values: {
      GOOGLEPAY: 'gpay_button',
      APPLEPAY: 'applepay_button',
      PAYPAL: 'paypal_button',
    },
  },
  ORIGIN: {
    name: 'origin',
    values: {
      NEW_COMPONENTS_PRICING_PAGE: 'new_components_pricing_page',
      NEW_COMPONENTS_CHECKOUT_MODAL: 'new_components_checkout_modal',
    },
  },
  USER_RECURLY_PLAN_NAME: {
    name: 'user_recurly_plan_name',
  },
  USER_PLAN_NAME: {
    name: 'user_plan_name',
    values: {
      INDIVIDUAL: 'individual',
      BUSINESS: 'business',
      ENTERPRISE: 'enterprise',
      RESTART: 'restart',
      FRESH_FREE: 'fresh_free',
      DOWNGRADED_FREE: 'downgraded_free',
    },
  },
  USER_PLAN_TIER: {
    name: 'user_plan_tier',
    values: {
      TIER1: 'tier1',
      TIER2: 'tier2',
      TIER3: 'tier3',
      RESTART: 'restart',
      FRESH_FREE: 'fresh_free',
      DOWNGRADED_FREE: 'downgraded_free',
    },
  },
  USER_BILLING_CYCLE: {
    name: 'user_billing_cycle',
    values: {
      MONTHLY: 'monthly',
      YEARLY: 'yearly',
      LIFETIME: 'lifetime',
    },
  },
  EXPERIMENT_COMPARING_PLANS_CHECKOUT: {
    name: 'experiment_comparing_plans_checkout',
    values: {
      TRUE: 'true',
      FALSE: 'false',
    },
  },
  CHECKOUT_MODAL_BUTTON_LOCATION: {
    name: 'checkout_modal_button_location',
    values: {
      TOP_PLAN_CARDS: 'top_plan_cards',
      PLANS_COMPARISON_TABLE: 'plans_comparison_table',
    },
  },
  MONTHLY_TIER_1_PRICE_EXPERIMENT: {
    name: 'experiment_reduceprice_monthlytier1_v2',
    values: {
      TRUE: 'true',
      FALSE: 'false',
      OUT_OF_EXPERIMENT: 'out-of-experiment',
    },
  },
  COUNTRY_GROUP: {
    name: 'country_group',
    values: {
      US: 'united_states',
      STANDARD: 'standard_countries',
      OTHERS: 'others',
    },
  },
  PURCHASE_TYPE: {
    name: 'purchase_type',
    values: {
      START_TRIAL: 'start_trial',
      UPGRADE_UPSELL: 'upgrade_upsell',
      UPGRADE_DOWNSELL: 'upgrade_downsell',
      DOWNGRADE_DOWNSELL: 'downgrade_downsell',
      DOWNGRADE_UPSELL: 'downgrade_upsell',
      UPSELL: 'upsell',
      DOWNSELL: 'downsell',
      UPGRADE: 'upgrade',
      DOWNGRADE: 'downgrade',
      WIN: 'win',
      REACTIVATION: 'reactivation',
    },
  },
  BUTTON_CLICKED_PROJECT_CREATION_SUCCESS: {
    name: 'button_clicked',
    values: {
      DONE: 'Done',
      ADD_KEYWORDS: 'Add Keywords',
      CLOSED: 'Closed',
    },
  },
  USER_DEVICE: {
    name: 'user_device',
    values: {
      DESKTOP: 'desktop',
      MOBILE: 'mobile',
    },
  },
  COPY_COUNT: {
    name: 'copy_count',
  },
  COPY_TYPE: {
    name: 'copy_type',
    values: {
      ALL: 'all',
      SELECTED: 'selected',
    },
  },
  EXPORT_COUNT: {
    name: 'export_count',
  },
  EXPORT_CSV_TYPE: {
    name: 'export_csv_type',
    values: {
      ALL: 'all',
      SELECTED: 'selected',
    },
  },
  UPLOAD_COUNT: {
    name: 'upload_count',
  },
}

export const ADDON_TYPE_TO_USAGE_BLOCK_MAP = {
  [allAddonsTypes.ADDON_DOMAIN]:
    MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION.values.ACCOUNT_BILLING,
  [allAddonsTypes.ADDON_TRACKEDKEYWORDS]:
    MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION.values.ACCOUNT_BILLING,
  [allAddonsTypes.ADDON_DAILYSEARCH]:
    MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION.values.ACCOUNT_BILLING,
  [allAddonsTypes.ADDON_USER]:
    MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION.values.ACCOUNT_BILLING,
  [allAddonsTypes.ADDON_COMPETITOR]:
    MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION.values.ACCOUNT_BILLING,
  [allAddonsTypes.ADDON_KEYWORD_METRICS_UPDATES]:
    MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION.values.ACCOUNT_BILLING,
  [allAddonsTypes.ADDON_AIW_BLOGGER]:
    MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION.values.AIW_WORD_COUNT_USAGE_CARD,
}

TIER_TO_PLAN_NAME_MAP = {
  tier1: MIXPANEL_PROPS.PLAN_NAME.values.INDIVIDUAL,
  tier2: MIXPANEL_PROPS.PLAN_NAME.values.BUSINESS,
  tier3: MIXPANEL_PROPS.PLAN_NAME.values.ENTERPRISE,
  restart: MIXPANEL_PROPS.PLAN_NAME.values.RESTART,
  tier0: MIXPANEL_PROPS.PLAN_NAME.values.ZERO,
}

// all mixpanel events definition
export const MIXPANEL_EVENTS = {
  PURCHASE_CLICK: {
    name: 'Purchase Click',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION, required: true },
      { prop: MIXPANEL_PROPS.BUTTON_CTA, required: true },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.SOURCE, required: true },
    ],
  },
  PLAN_SELECT_CLICK: {
    name: 'Plan Select Click',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.PLAN_NAME, required: true },
      { prop: MIXPANEL_PROPS.PLAN_TIER, required: true },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE, required: true },
      { prop: MIXPANEL_PROPS.PRICING_PAGE_BUTTON_LOCATION, required: true },
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.SOURCE, required: true },
      { prop: MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION, required: true },
      { prop: MIXPANEL_PROPS.CHECKOUT_MODAL_BUTTON_LOCATION },
    ],
  },
  CHECKOUT_VIEW: {
    name: 'Checkout View',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.PLAN_NAME, required: true },
      { prop: MIXPANEL_PROPS.PLAN_TIER, required: true },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE, required: true },
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.USD_VALUE },
      { prop: MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION, required: true },
      { prop: MIXPANEL_PROPS.SOURCE },
    ],
  },
  AIW_CHECKOUT_VIEW: {
    name: 'AIW Checkout View',
    enabled: true,
    props: [{ prop: MIXPANEL_PROPS.AIW_USER_STATUS }],
  },
  PAYMENT_SUBMIT_CLICK: {
    name: 'Payment Submit Click',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.PLAN_NAME, required: true },
      { prop: MIXPANEL_PROPS.PLAN_TIER, required: true },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE, required: true },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.PAYMENT_METHOD, required: true },
      { prop: MIXPANEL_PROPS.PAYMENT_STATUS, required: true },
      { prop: MIXPANEL_PROPS.PAYMENT_ISSUE_DESCRIPTION },
      { prop: MIXPANEL_PROPS.PAYMENT_ERROR_MESSAGE },
      { prop: MIXPANEL_PROPS.SOURCE, required: true },
      { prop: MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION, required: true },
    ],
  },
  CHECKOUT_SUCCESS: {
    name: 'Checkout Success',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.PLAN_NAME, required: true },
      { prop: MIXPANEL_PROPS.PLAN_TIER, required: true },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE, required: true },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.PAYMENT_METHOD, required: true },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.USD_VALUE, required: true },
      { prop: MIXPANEL_PROPS.SOURCE, required: true },
      { prop: MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION, required: true },
      { prop: MIXPANEL_PROPS.PURCHASE_TYPE, required: true },
    ],
  },
  CHECKOUT_ERROR: {
    name: 'Checkout Error',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.PLAN_NAME, required: true },
      { prop: MIXPANEL_PROPS.PLAN_TIER, required: true },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE, required: true },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.PAYMENT_METHOD, required: true },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.USD_VALUE, required: true },
      { prop: MIXPANEL_PROPS.SOURCE, required: true },
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.PAYMENT_ERROR_MESSAGE },
      { prop: MIXPANEL_PROPS.PAYMENT_ERROR_PARAM },
    ],
  },
  PROJECT_CREATION_CLICK: {
    name: 'Project Creation Click',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.ADD_PROJECT_BUTTON_LOCATION, required: true },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.PAYMENT_METHOD },
    ],
  },
  PROJECT_DOMAIN_INPUT_SUCCESS: {
    name: 'Project Domain Input Success',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.ADD_PROJECT_BUTTON_LOCATION, required: true },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.PAYMENT_METHOD },
    ],
  },
  PROJECT_LANGUAGE_AND_LOCATION_ADD_SUCCESS: {
    name: 'Project Language And Location Add Success',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.ADDED_PROJECT_LANGUAGES, required: true },
      { prop: MIXPANEL_PROPS.PROJECT_LANGUAGES_COUNT, required: true },
      { prop: MIXPANEL_PROPS.ADDED_PROJECT_LOCATIONS, required: true },
      { prop: MIXPANEL_PROPS.PROJECT_LOCATIONS_COUNT, required: true },
      { prop: MIXPANEL_PROPS.ADD_PROJECT_BUTTON_LOCATION, required: true },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.PAYMENT_METHOD },
    ],
  },
  PROJECT_KEYWORDS_ADD_SUCCESS: {
    name: 'Project Keywords Add Success',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.PROJECT_KEYWORDS_COUNT, required: true },
      { prop: MIXPANEL_PROPS.SKIPPED_ADD_KEYWORDS, required: true },
      { prop: MIXPANEL_PROPS.ADDED_PROJECT_LANGUAGES, required: true },
      { prop: MIXPANEL_PROPS.PROJECT_LANGUAGES_COUNT, required: true },
      { prop: MIXPANEL_PROPS.ADDED_PROJECT_LOCATIONS, required: true },
      { prop: MIXPANEL_PROPS.PROJECT_LOCATIONS_COUNT, required: true },
      { prop: MIXPANEL_PROPS.ADD_PROJECT_BUTTON_LOCATION, required: true },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.PAYMENT_METHOD },
    ],
  },
  PROJECT_COMPETITORS_ADD_SUCCESS: {
    name: 'Project Competitors Add Success',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.PROJECT_COMPETITORS_COUNT, required: true },
      { prop: MIXPANEL_PROPS.SKIPPED_ADD_COMPETITORS, required: true },
      { prop: MIXPANEL_PROPS.PROJECT_KEYWORDS_COUNT, required: true },
      { prop: MIXPANEL_PROPS.SKIPPED_ADD_KEYWORDS, required: true },
      { prop: MIXPANEL_PROPS.ADDED_PROJECT_LANGUAGES, required: true },
      { prop: MIXPANEL_PROPS.PROJECT_LANGUAGES_COUNT, required: true },
      { prop: MIXPANEL_PROPS.ADDED_PROJECT_LOCATIONS, required: true },
      { prop: MIXPANEL_PROPS.PROJECT_LOCATIONS_COUNT, required: true },
      { prop: MIXPANEL_PROPS.ADD_PROJECT_BUTTON_LOCATION, required: true },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.PAYMENT_METHOD },
    ],
  },
  PROJECT_CREATION_SUCCESS: {
    name: 'Project Creation Success',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.PROJECT_AGENCY_OFFER_RESPONSE, required: true },
      { prop: MIXPANEL_PROPS.PROJECT_COMPETITORS_COUNT, required: true },
      { prop: MIXPANEL_PROPS.SKIPPED_ADD_COMPETITORS, required: true },
      { prop: MIXPANEL_PROPS.PROJECT_KEYWORDS_COUNT, required: true },
      { prop: MIXPANEL_PROPS.SKIPPED_ADD_KEYWORDS, required: true },
      { prop: MIXPANEL_PROPS.ADDED_PROJECT_LANGUAGES, required: true },
      { prop: MIXPANEL_PROPS.PROJECT_LANGUAGES_COUNT, required: true },
      { prop: MIXPANEL_PROPS.ADDED_PROJECT_LOCATIONS, required: true },
      { prop: MIXPANEL_PROPS.PROJECT_LOCATIONS_COUNT, required: true },
      { prop: MIXPANEL_PROPS.ADD_PROJECT_BUTTON_LOCATION, required: true },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.PAYMENT_METHOD },
    ],
  },
  CREATE_PROJECT_MODAL_VIEW: {
    name: 'Create Project Modal View',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.PAYMENT_METHOD },
    ],
  },
  METRIC_CLICK: {
    name: 'Metric Click',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.APP_SECTION },
      { prop: MIXPANEL_PROPS.METRIC_SELECTED, required: true },
    ],
  },
  VIEW_ALL_REPORT_CLICK: {
    name: 'View All Report Click',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.APP_SECTION },
      { prop: MIXPANEL_PROPS.WIDGET_SELECTED, required: true },
    ],
  },
  EXPAND_DATA_CLICK: {
    name: 'Expand Data Click',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.APP_SECTION },
      { prop: MIXPANEL_PROPS.WIDGET_SELECTED, required: true },
      { prop: MIXPANEL_PROPS.DISPLAYED_METRIC, required: true },
      { prop: MIXPANEL_PROPS.ROW_POSITION, required: true },
    ],
  },
  SERP_FEATURES_TOGGLE_CLICK: {
    name: 'SERP Features Toggle Click',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.APP_SECTION },
      { prop: MIXPANEL_PROPS.WIDGET_SELECTED, required: true },
      { prop: MIXPANEL_PROPS.SERP_FEATURES, required: true },
      { prop: MIXPANEL_PROPS.TOGGLE_STATUS, required: true },
    ],
  },
  WANT_NPD_TEAM_GROW_TRAFFIC_CLICK: {
    name: 'Want NPD Team Grow Traffic Click',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.APP_SECTION },
      { prop: MIXPANEL_PROPS.PROJECT_COMPETITORS_COUNT, required: true },
      { prop: MIXPANEL_PROPS.SKIPPED_ADD_COMPETITORS, required: true },
      { prop: MIXPANEL_PROPS.PROJECT_KEYWORDS_COUNT, required: true },
      { prop: MIXPANEL_PROPS.SKIPPED_ADD_KEYWORDS, required: true },
      { prop: MIXPANEL_PROPS.ADDED_PROJECT_LANGUAGES, required: true },
      { prop: MIXPANEL_PROPS.PROJECT_LANGUAGES_COUNT, required: true },
      { prop: MIXPANEL_PROPS.ADDED_PROJECT_LOCATIONS, required: true },
      { prop: MIXPANEL_PROPS.PROJECT_LOCATIONS_COUNT, required: true },
      { prop: MIXPANEL_PROPS.ADD_PROJECT_BUTTON_LOCATION, required: true },
    ],
  },
  NPD_CONSULTING_CLICK: {
    name: 'NPD Consulting Click',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.APP_SECTION },
      { prop: MIXPANEL_PROPS.MENU_SUBITEM_NAME },
      { prop: MIXPANEL_PROPS.BUTTON_LOCATION },
    ],
  },
  NPD_TEAM_GROW_TRAFFIC_LEAD_SUCCESS: {
    name: 'NPD Team Grow Traffic Lead Success',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.APP_SECTION },
      { prop: MIXPANEL_PROPS.PROJECT_COMPETITORS_COUNT, required: true },
      { prop: MIXPANEL_PROPS.SKIPPED_ADD_COMPETITORS, required: true },
      { prop: MIXPANEL_PROPS.PROJECT_KEYWORDS_COUNT, required: true },
      { prop: MIXPANEL_PROPS.SKIPPED_ADD_KEYWORDS, required: true },
      { prop: MIXPANEL_PROPS.ADDED_PROJECT_LANGUAGES, required: true },
      { prop: MIXPANEL_PROPS.PROJECT_LANGUAGES_COUNT, required: true },
      { prop: MIXPANEL_PROPS.ADDED_PROJECT_LOCATIONS, required: true },
      { prop: MIXPANEL_PROPS.PROJECT_LOCATIONS_COUNT, required: true },
      { prop: MIXPANEL_PROPS.ADD_PROJECT_BUTTON_LOCATION, required: true },
      { prop: MIXPANEL_PROPS.BUSINESS_TYPE, required: true },
      { prop: MIXPANEL_PROPS.CHALLENGES, required: true },
      { prop: MIXPANEL_PROPS.NUMBER_OF_EMPLOYEES, required: true },
      { prop: MIXPANEL_PROPS.URGENCY_FOR_HELP, required: true },
      { prop: MIXPANEL_PROPS.INDUSTRY, required: true },
      { prop: MIXPANEL_PROPS.MONTHLY_MARKETING_BUDGET, required: true },
    ],
  },
  LOADED_REPORT_VIEW: {
    name: 'Loaded Report View',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.APP_SECTION },
      { prop: MIXPANEL_PROPS.CONTAIN_RESULTS },
      { prop: MIXPANEL_PROPS.DAYS_SINCE_SIGNED_UP },
      { prop: MIXPANEL_PROPS.DAYS_SINCE_SUBSCRIBED },
      { prop: MIXPANEL_PROPS.USER_PARENT },
      { prop: MIXPANEL_PROPS.LANDING_PAGE_DASHBOARD_BUTTON_CLICKED },
      { prop: MIXPANEL_PROPS.EXPERIMENT_LANDING_PAGE_DASHBOARD },
    ],
  },
  UNLOADED_REPORT_VIEW: {
    name: 'Unloaded Report View',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.APP_SECTION },
      { prop: MIXPANEL_PROPS.DAYS_SINCE_SIGNED_UP },
      { prop: MIXPANEL_PROPS.DAYS_SINCE_SUBSCRIBED },
      { prop: MIXPANEL_PROPS.USER_PARENT },
      { prop: MIXPANEL_PROPS.LANDING_PAGE_DASHBOARD_BUTTON_CLICKED },
      { prop: MIXPANEL_PROPS.EXPERIMENT_LANDING_PAGE_DASHBOARD },
    ],
  },
  SEARCH_SUCCESS: {
    name: 'Search Success',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.SELECTED_LOCATION, required: true },
      { prop: MIXPANEL_PROPS.SELECTED_LANGUAGE, required: true },
      { prop: MIXPANEL_PROPS.SEARCH_RESULTS_COUNT, required: true },
      { prop: MIXPANEL_PROPS.SEARCH_INPUT, required: true },
      { prop: MIXPANEL_PROPS.SEARCH_KEYWORDS_INPUT_COUNT },
      { prop: MIXPANEL_PROPS.SEARCH_INPUT_LENGTH, required: true },
      { prop: MIXPANEL_PROPS.APP_SECTION },
      { prop: MIXPANEL_PROPS.SEARCH_INPUT_TERM, required: true },
    ],
  },
  AIW_CHECKOUT_SUCCESS: {
    name: 'AIW Checkout Success',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.AIW_USER_STATUS },
      { prop: MIXPANEL_PROPS.USD_VALUE, required: true },
    ],
  },
  AIW_ORDER_BUMP_VIEW: {
    name: 'AIW Order Bump View',
    enabled: true,
    props: [{ prop: MIXPANEL_PROPS.AIW_USER_STATUS }],
  },
  AIW_ORDER_BUMP_SUCCESS: {
    name: 'AIW Order Bump Success',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.AIW_USER_STATUS },
      { prop: MIXPANEL_PROPS.USD_VALUE, required: true },
    ],
  },
  AIW_ACCOUNT_BILLING_VIEW: {
    name: 'AIW Account & Billing View',
    enabled: true,
    props: [{ prop: MIXPANEL_PROPS.AIW_USER_STATUS }],
  },
  AIW_ACCOUNT_BILLING_SUCCESS: {
    name: 'AIW Account & Billing Success',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.AIW_USER_STATUS },
      { prop: MIXPANEL_PROPS.USD_VALUE, required: true },
    ],
  },
  AIW_ADDON_REMOVE_SUCCESS: {
    name: 'AIW Addon Removed Success',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.AIW_USER_STATUS },
      { prop: MIXPANEL_PROPS.USD_VALUE, required: true },
    ],
  },
  ATP_CROSS_SELL_MODAL_CLICK: {
    name: 'ATP Cross-Sell Modal Click',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
    ],
  },
  SIGN_IN_PAGEVIEW: {
    name: 'Sign In Pageview',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.PAGE_NAME, required: true },
      { prop: MIXPANEL_PROPS.SOURCE },
    ],
  },
  SIGN_UP_PAGEVIEW: {
    name: 'Sign Up Pageview',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.PAGE_NAME, required: true },
      { prop: MIXPANEL_PROPS.SOURCE },
    ],
  },
  SIGN_UP_SUCCESS: {
    name: 'Sign Up Success',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD, required: true },
      { prop: MIXPANEL_PROPS.PAGE_NAME, required: true },
      { prop: MIXPANEL_PROPS.SOURCE, required: true },
    ],
  },
  SIGN_IN_SUCCESS: {
    name: 'Sign In Success',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD, required: true },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.PAYMENT_METHOD },
      { prop: MIXPANEL_PROPS.SOURCE, required: true },
      { prop: MIXPANEL_PROPS.MONTHLY_TIER_1_PRICE_EXPERIMENT },
    ],
  },
  SITE_AUDIT_SUCCESS: {
    name: 'Site Audit Success',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.AUDIT_COUNT_PAGES_CRAWLED, required: true },
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
    ],
  },
  BUY_ADDON_CLICK: {
    name: 'Buy Add-on Click',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.PAYMENT_METHOD },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION },
      { prop: MIXPANEL_PROPS.ADDON_COUNT },
      { prop: MIXPANEL_PROPS.ADDON_ON_LIST },
    ],
  },
  EXPORT_SUCCESS: {
    name: 'Export Success',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.APP_SECTION },
      { prop: MIXPANEL_PROPS.EXPORT_TYPE, required: true },
      { prop: MIXPANEL_PROPS.EXPORTED_CHART },
      { prop: MIXPANEL_PROPS.EXPORT_COUNT, required: true },
      { prop: MIXPANEL_PROPS.EXPORT_CSV_TYPE, required: true },
    ],
  },
  LOCATION_BREAKDOWN_SELECT_CLICK: {
    name: 'Location Breakdown Select Click',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.APP_SECTION },
      { prop: MIXPANEL_PROPS.SELECTED_LOCATION_BREAKDOWN, required: true },
    ],
  },
  URL_EXTERNAL_REDIRECTION_CLICK: {
    name: 'URL External Redirection Click',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.APP_SECTION },
      { prop: MIXPANEL_PROPS.WIDGET_SELECTED, required: true },
      { prop: MIXPANEL_PROPS.ROW_POSITION, required: true },
    ],
  },

  get ADDON_CONFIRMATION_VIEW() {
    return {
      ...this.BUY_ADDON_CLICK,
      name: 'Buy Add-on Confirmation View',
      enabled: true,
    }
  },

  get BUY_ADDON_SUCCESS() {
    return {
      ...this.BUY_ADDON_CLICK,
      name: 'Buy Add-on Success',
      enabled: true,
    }
  },

  get CANCEL_ADDON_SUCCSESS() {
    return {
      ...this.BUY_ADDON_CLICK,
      name: 'Cancel Add-on Success',
      enabled: true,
    }
  },
  PAY_WITH_PAYPAL_CLICK: {
    name: 'Pay With Paypal Click',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.PAGE_NAME, required: true },
      { prop: MIXPANEL_PROPS.PLAN_NAME, required: true },
      { prop: MIXPANEL_PROPS.PLAN_TIER, required: true },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE, required: true },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.SOURCE, required: true },
      { prop: MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION, required: true },
    ],
  },
  PRICING_PAGE_VIEW: {
    name: 'Pricing Page View',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.PAGE_NAME, required: true },
      { prop: MIXPANEL_PROPS.SOURCE, required: true },
    ],
  },
  CUSTOM_PLAN_CLICK: {
    name: 'Custom Plan Click',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.PAGE_NAME, required: true },
    ],
  },
  CUSTOM_PLAN_FORM_SUBMIT_SUCCESS: {
    name: 'Custom Plan Form Submit Success',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.PAGE_NAME, required: true },
    ],
  },
  EXIT_INTENT_MODAL_VIEW: {
    name: 'Exit Intent modal view',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.PROMOTION },
      { prop: MIXPANEL_PROPS.USER_STATUS },
    ],
  },
  EXIT_INTENT_SUCCESS: {
    name: 'Exit intent success',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.PROMOTION },
      { prop: MIXPANEL_PROPS.SESSION_NUMBER },
      { prop: MIXPANEL_PROPS.USER_STATUS },
    ],
  },
  INTENT_MODAL_EXIT: {
    name: 'Intent modal exit',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.PROMOTION },
      { prop: MIXPANEL_PROPS.USER_STATUS },
    ],
  },
  NEW_CHECKOUT_MODAL_VIEW: {
    name: 'New Checkout Modal View',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.PAYMENT_METHOD },
      { prop: MIXPANEL_PROPS.SOURCE, required: true },
      { prop: MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION, required: true },
    ],
  },

  PAYMENT_SELECT_CLICK: {
    name: 'Payment Select Click',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.PLAN_NAME, required: true },
      { prop: MIXPANEL_PROPS.PLAN_TIER, required: true },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE, required: true },
      { prop: MIXPANEL_PROPS.PAYMENT_METHOD, required: true },
      { prop: MIXPANEL_PROPS.SOURCE, required: true },
      { prop: MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION, required: true },
    ],
  },
  CANCEL_SUBSCRIPTION_SUCCESS: {
    name: 'Cancel Subscription Success',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.PLAN_NAME, required: true },
      { prop: MIXPANEL_PROPS.PLAN_TIER, required: true },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE, required: true },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.PAYMENT_METHOD, required: true },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.CANCELLATION_FEEDBACK, required: true },
      { prop: MIXPANEL_PROPS.CANCELLATION_REASON, required: true },
      { prop: MIXPANEL_PROPS.USER_STATUS },
    ],
  },
  SEND_FEEDBACK_SUCESS: {
    name: 'Send Feedback Sucess',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.FEEDBACK, required: true },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.PAYMENT_METHOD },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.APP_SECTION },
      { prop: MIXPANEL_PROPS.USER_STATUS },
    ],
  },
  SUGGEST_FEATURE_REDIRECTION_SUCCESS: {
    name: 'Suggest a Feature Redirection Success',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.PAYMENT_METHOD },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.APP_SECTION },
    ],
  },
  TREND_SELECTION_CLICK: {
    name: 'Trend Selection Click',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.APP_SECTION },
      { prop: MIXPANEL_PROPS.TREND_SELECTED, required: true },
    ],
  },
  TAB_SELECTION_CLICK: {
    name: 'Tab Selection Click',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.APP_SECTION },
      { prop: MIXPANEL_PROPS.TAB_SELECTED, required: true },
    ],
  },
  CONNECT_WITH_GOOGLE_TOOLS_CLICK: {
    name: 'Connect with Google Tools Click',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      {
        prop: MIXPANEL_PROPS.CONNECT_WITH_GOOGLE_BUTTON_LOCATION,
        required: true,
      },
    ],
  },
  CONNECT_WITH_GOOGLE_TOOLS_SUCCESS: {
    name: 'Connect with Google Tools Success ',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      {
        prop: MIXPANEL_PROPS.CONNECT_WITH_GOOGLE_BUTTON_LOCATION,
        required: true,
      },
      { prop: MIXPANEL_PROPS.GOOGLE_TOOL_CONNECTED, required: true },
    ],
  },
  CREATE_KEYWORD_LIST_SUCCESS: {
    name: 'Create Keyword List Success',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.KEYWORD_IN_LIST_COUNT },
      { prop: MIXPANEL_PROPS.SELECTED_LOCATION, required: true },
      { prop: MIXPANEL_PROPS.SELECTED_LANGUAGE, required: true },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.APP_SECTION },
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
    ],
  },
  UPDATE_KEYWORD_CLICK: {
    name: 'Update Keyword Click',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.APP_SECTION },
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.UPDATE_METRIC, required: true },
      { prop: MIXPANEL_PROPS.UPDATE_KEYWORDS_COUNT, required: true },
      { prop: MIXPANEL_PROPS.UPDATE_TYPE, required: true },
    ],
  },
  ADD_KEYWORD_SUCCESS: {
    name: 'Add Keyword Success',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.ADDED_KEYWORDS_COUNT },
      { prop: MIXPANEL_PROPS.ADDED_LISTS_COUNT },
      { prop: MIXPANEL_PROPS.ADDED_KEYWORDS_TYPE },
      { prop: MIXPANEL_PROPS.SELECTED_LOCATION },
      { prop: MIXPANEL_PROPS.SELECTED_LANGUAGE },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.APP_SECTION },
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
    ],
  },
  UPDATE_KEYWORD_SUCCESS: {
    name: 'Update Keyword Success',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.APP_SECTION },
      { prop: MIXPANEL_PROPS.UPDATE_KEYWORDS_COUNT, required: true },
      { prop: MIXPANEL_PROPS.UPDATE_TYPE, required: true },
    ],
  },
  SEO_OPPORTUNITIES_PREFERENCE_CLICK: {
    name: 'SEO Opportunities Preference Click',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.OPPORTUNITY_TYPE, required: true },
      { prop: MIXPANEL_PROPS.PREFERENCE, required: true },
      { prop: MIXPANEL_PROPS.ROW_POSITION, required: true },
    ],
  },
  SEO_OPPORTUNITIES_MARK_DONE_CLICK: {
    name: 'SEO Opportunities Mark Done Click',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.OPPORTUNITY_TYPE, required: true },
      { prop: MIXPANEL_PROPS.ROW_POSITION, required: true },
    ],
  },
  SEO_OPPORTUNITIES_START_CLICK: {
    name: 'SEO Opportunities Start Click',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.OPPORTUNITY_TYPE, required: true },
      { prop: MIXPANEL_PROPS.ROW_POSITION, required: true },
    ],
  },
  SEARCH_SUGGESTIONS_CLICK: {
    name: 'Search Suggestions Click',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.APP_SECTION },
    ],
  },
  ONBOARDING_QUESTIONNAIRE_EXPERIMENT_PAGE_VIEW: {
    name: 'Onboarding Questionnaire Experiment Page View',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.DAYS_SINCE_SIGNED_UP },
      { prop: MIXPANEL_PROPS.DAYS_SINCE_SUBSCRIBED },
      { prop: MIXPANEL_PROPS.USER_PARENT },
    ],
  },
  ONBOARDING_QUESTIONNAIRE_COMPLETE: {
    name: 'Onboarding Questionnaire Complete',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.ONBOARDING_QUESTIONNAIRE_NAME },
    ],
  },
  PAYMENT_BUTTON_CLICKED: {
    name: 'Payment Button Clicked',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION, required: true },
      { prop: MIXPANEL_PROPS.BUTTON_NAME, required: true },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.SOURCE },
    ],
  },
  PLAIN_FILTER_CLICK: {
    name: 'Apply Plain Filter Click',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.APP_SECTION },
      { prop: MIXPANEL_PROPS.FILTERED_METRIC, required: true },
      { prop: MIXPANEL_PROPS.FILTERED_VALUE, required: true },
    ],
  },
  SORT_CLICK: {
    name: 'Sort Click',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.APP_SECTION },
      { prop: MIXPANEL_PROPS.SORTED_COLUMN, required: true },
      { prop: MIXPANEL_PROPS.SORTED_TYPE, required: true },
    ],
  },
  CHANGE_PAGINATION_CLICK: {
    name: 'Change Pagination Click',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.APP_SECTION },
      { prop: MIXPANEL_PROPS.PAGINATION_TYPE, required: true },
    ],
  },
  CHOOSE_YOUR_PLAN_VIEW: {
    name: 'Choose Your Plan View',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.SOURCE },
      { prop: MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION },
      { prop: MIXPANEL_PROPS.USD_VALUE },
    ],
  },
  CARD_DETAILS_VIEW: {
    name: 'Card Details View',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.SOURCE },
      { prop: MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION },
      { prop: MIXPANEL_PROPS.USD_VALUE },
    ],
  },
  CONGRATULATIONS_VIEW: {
    name: 'Congratulations View',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.SOURCE },
      { prop: MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION },
      { prop: MIXPANEL_PROPS.USD_VALUE },
    ],
  },
  ATP_CROSS_SELL_MODAL_VIEW: {
    name: 'ATP Cross-sell Modal View',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.SOURCE },
      { prop: MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION },
      { prop: MIXPANEL_PROPS.USD_VALUE },
    ],
  },
  PROJECT_CREATION_SUCCESS_MODAL_CLICK: {
    name: 'Project Creation Success Modal Click',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.USER_PRICING_GROUP },
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.PAYMENT_METHOD },
      {
        prop: MIXPANEL_PROPS.BUTTON_CLICKED_PROJECT_CREATION_SUCCESS,
      },
    ],
  },
  UPDATE_BILLING_INFORMATION_MODAL_VIEW: {
    name: 'Update Billing Information Modal View',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.PAGE_NAME, required: true },
      { prop: MIXPANEL_PROPS.PLAN_NAME, required: true },
      { prop: MIXPANEL_PROPS.PLAN_TIER, required: true },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE, required: true },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.PAYMENT_METHOD },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.USER_PRICING_GROUP },
      { prop: MIXPANEL_PROPS.SOURCE },
    ],
  },
  UPDATE_BILLING_INFORMATION_ERROR: {
    name: 'Update Billing Information Error',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.PAGE_NAME, required: true },
      { prop: MIXPANEL_PROPS.PLAN_NAME, required: true },
      { prop: MIXPANEL_PROPS.PLAN_TIER, required: true },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE, required: true },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.PAYMENT_METHOD },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.USER_PRICING_GROUP },
      { prop: MIXPANEL_PROPS.PAYMENT_DATE },
      { prop: MIXPANEL_PROPS.USD_VALUE },
      { prop: MIXPANEL_PROPS.SOURCE },
    ],
  },
  UPDATE_BILLING_INFORMATION_UPDATED: {
    name: 'Update Billing Information Updated',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.PAGE_NAME, required: true },
      { prop: MIXPANEL_PROPS.PLAN_NAME, required: true },
      { prop: MIXPANEL_PROPS.PLAN_TIER, required: true },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE, required: true },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.PAYMENT_METHOD },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.USER_PRICING_GROUP },
      { prop: MIXPANEL_PROPS.PAYMENT_DATE },
      { prop: MIXPANEL_PROPS.SOURCE },
    ],
  },
  COPY_SUCCESS: {
    name: 'Copy Success',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.COPY_COUNT, required: true },
      { prop: MIXPANEL_PROPS.COPY_TYPE, required: true },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.APP_SECTION },
    ],
  },
  CHANGE_EMAIL_CLICK: {
    name: 'Change Email: Click',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.PAYMENT_METHOD },
      { prop: MIXPANEL_PROPS.SOURCE },

      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
    ],
  },
  CHOOSE_YOUR_SUCCESS_PATH_VIEW: {
    name: 'Choose Your Success Path View',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.APP_SECTION },
      { prop: MIXPANEL_PROPS.PROJECT_COMPETITORS_COUNT, required: true },
      { prop: MIXPANEL_PROPS.SKIPPED_ADD_COMPETITORS, required: true },
      { prop: MIXPANEL_PROPS.PROJECT_KEYWORDS_COUNT, required: true },
      { prop: MIXPANEL_PROPS.SKIPPED_ADD_KEYWORDS, required: true },
      { prop: MIXPANEL_PROPS.ADDED_PROJECT_LANGUAGES, required: true },
      { prop: MIXPANEL_PROPS.PROJECT_LANGUAGES_COUNT, required: true },
      { prop: MIXPANEL_PROPS.ADDED_PROJECT_LOCATIONS, required: true },
      { prop: MIXPANEL_PROPS.PROJECT_LOCATIONS_COUNT, required: true },
      { prop: MIXPANEL_PROPS.ADD_PROJECT_BUTTON_LOCATION, required: true },
    ],
  },
  CHOOSE_YOUR_SUCCESS_PATH_CTA_CLICKED: {
    name: 'Choose Your Success Path CTA Clicked',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.USER_STATUS },
      { prop: MIXPANEL_PROPS.USER_CURRENT_PLAN },
      { prop: MIXPANEL_PROPS.PLAN_NAME },
      { prop: MIXPANEL_PROPS.PLAN_TIER },
      { prop: MIXPANEL_PROPS.BILLING_CYCLE },
      { prop: MIXPANEL_PROPS.SIGN_IN_METHOD },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.APP_SECTION },
      { prop: MIXPANEL_PROPS.PROJECT_COMPETITORS_COUNT, required: true },
      { prop: MIXPANEL_PROPS.SKIPPED_ADD_COMPETITORS, required: true },
      { prop: MIXPANEL_PROPS.PROJECT_KEYWORDS_COUNT, required: true },
      { prop: MIXPANEL_PROPS.SKIPPED_ADD_KEYWORDS, required: true },
      { prop: MIXPANEL_PROPS.ADDED_PROJECT_LANGUAGES, required: true },
      { prop: MIXPANEL_PROPS.PROJECT_LANGUAGES_COUNT, required: true },
      { prop: MIXPANEL_PROPS.ADDED_PROJECT_LOCATIONS, required: true },
      { prop: MIXPANEL_PROPS.PROJECT_LOCATIONS_COUNT, required: true },
      { prop: MIXPANEL_PROPS.ADD_PROJECT_BUTTON_LOCATION, required: true },
      { prop: MIXPANEL_PROPS.ONBOARDING_SUCCESS_PATH, required: true },
    ],
  },
  get CHANGE_EMAIL_OLD_EMAIL_PASSWORD_WRONG() {
    return {
      ...this.CHANGE_EMAIL_CLICK,
      name: 'Change Email: Old Email Password Wrong',
      enabled: true,
    }
  },
  get CHANGE_EMAIL_SEND_CODE_TO_OLD_EMAIL() {
    return {
      ...this.CHANGE_EMAIL_CLICK,
      name: 'Change Email: Send Code to Old Email',
      enabled: true,
    }
  },
  get CHANGE_EMAIL_CONTACT_US_OLD_EMAIL() {
    return {
      ...this.CHANGE_EMAIL_CLICK,
      name: 'Change Email: Contact Us (Old Email)',
      enabled: true,
    }
  },
  get CHANGE_EMAIL_OLD_EMAIL_VALIDATED() {
    return {
      ...this.CHANGE_EMAIL_CLICK,
      name: 'Change Email: Old Email Validated',
      enabled: true,
    }
  },
  get CHANGE_EMAIL_OLD_EMAIL_CODE_ERROR() {
    return {
      ...this.CHANGE_EMAIL_CLICK,
      name: 'Change Email: Old Email Code Error',
      enabled: true,
    }
  },
  get CHANGE_EMAIL_RESEND_CODE_OLD_EMAIL() {
    return {
      ...this.CHANGE_EMAIL_CLICK,
      name: 'Change Email: Resend Code Old Email',
      enabled: true,
    }
  },
  get CHANGE_EMAIL_MAX_ATTEMPTS_EXCEEDED_OLD_EMAIL_USER_BLOCKED() {
    return {
      ...this.CHANGE_EMAIL_CLICK,
      name: 'Change Email: Max attempts exceeded (Old Email) User blocked',
      enabled: true,
    }
  },
  get CHANGE_EMAIL_MAX_ATTEMPTS_EXCEEDED_OLD_EMAIL_CONTACT_SUPPORT() {
    return {
      ...this.CHANGE_EMAIL_CLICK,
      name: 'Change Email: Max attempts exceeded (Old Email) Contact Support',
      enabled: true,
    }
  },
  get CHANGE_EMAIL_OLD_EMAIL_CODE_VALIDATED() {
    return {
      ...this.CHANGE_EMAIL_CLICK,
      name: 'Change Email: Old Email Code Validated',
      enabled: true,
    }
  },
  get CHANGE_EMAIL_SEND_CODE_TO_NEW_EMAIL() {
    return {
      ...this.CHANGE_EMAIL_CLICK,
      name: 'Change Email: Send Code to New Email',
      enabled: true,
    }
  },
  get CHANGE_EMAIL_NEW_EMAIL_CODE_ERROR() {
    return {
      ...this.CHANGE_EMAIL_CLICK,
      name: 'Change Email: New Email Code Error',
      enabled: true,
    }
  },
  get CHANGE_EMAIL_RESEND_CODE_NEW_EMAIL() {
    return {
      ...this.CHANGE_EMAIL_CLICK,
      name: 'Change Email: Resend Code New Email',
      enabled: true,
    }
  },
  get CHANGE_EMAIL_MAX_ATTEMPTS_EXCEEDED_NEW_EMAIL_USER_BLOCKED() {
    return {
      ...this.CHANGE_EMAIL_CLICK,
      name: 'Change Email: Max attempts exceeded (New Email) User blocked',
      enabled: true,
    }
  },
  get CHANGE_EMAIL_MAX_ATTEMPTS_EXCEEDED_NEW_EMAIL_CONTACT_SUPPORT() {
    return {
      ...this.CHANGE_EMAIL_CLICK,
      name: 'Change Email: Max attempts exceeded (New Email) Contact Support',
      enabled: true,
    }
  },
  get CHANGE_EMAIL_NEW_EMAIL_UPDATED() {
    return {
      ...this.CHANGE_EMAIL_CLICK,
      name: 'Change Email: New Email Updated',
      enabled: true,
    }
  },
  get CHANGE_EMAIL_CLICK_ALERT_CONFIRM_EMAIL() {
    return {
      ...this.CHANGE_EMAIL_CLICK,
      name: 'Change Email: Click Alert Confirm Email',
      enabled: true,
    }
  },
  get CHANGE_EMAIL_CLICK_BANNER_CONFIRM_EMAIL() {
    return {
      ...this.CHANGE_EMAIL_CLICK,
      name: 'Change Email: Click Banner Confirm Email',
      enabled: true,
    }
  },
  get CHANGE_EMAIL_NOTIFICATION_CONFIRM_NEW_EMAIL() {
    return {
      ...this.CHANGE_EMAIL_CLICK,
      name: 'Change Email: Notification Confirm New Email',
      enabled: true,
    }
  },
  UPLOAD_CSV_SUCCESS: {
    name: 'Upload CSV Success',
    enabled: true,
    props: [
      { prop: MIXPANEL_PROPS.UPLOAD_COUNT, required: true },
      { prop: MIXPANEL_PROPS.PAGE_NAME },
      { prop: MIXPANEL_PROPS.APP_SECTION },
    ],
  },
}

export const Mixpanel = {
  identify: (id = 'anonymous') => {
    if (REACT_APP_MIXPANEL_API_KEY) {
      mixpanel.identify(id)
    }
  },

  reset: () => {
    if (REACT_APP_MIXPANEL_API_KEY) {
      mixpanel.reset()
    }
  },

  alias: (id) => {
    if (REACT_APP_MIXPANEL_API_KEY) {
      mixpanel.alias(id)
    }
  },

  setPeople: (props) => {
    if (REACT_APP_MIXPANEL_API_KEY) {
      mixpanel.people.set(props)
    }
  },

  registerAndSetPeople: (props) => {
    Mixpanel.setPeople(props)
    Mixpanel.register(props)
  },

  setUserProperties: (user) => {
    const { name, id, created_at, payment_account_created_at } = user
    const {
      DAYS_SINCE_SIGNED_UP,
      DAYS_SINCE_STARTED_TRIAL,
      DAYS_SINCE_SUBSCRIBED,
      USER_STATUS,
      USER_BACKEND_ID,
      USER_BILLING_CYCLE,
      USER_PARENT,
      USER_PLAN_NAME,
      USER_PLAN_TIER,
      USER_RECURLY_PLAN_NAME,
      USER_PRICING_GROUP,
      SIGN_IN_METHOD,
      SIGNED_UP_DATE,
      START_TRIAL_DATE,
      USER_DEVICE,
    } = MIXPANEL_PROPS

    Mixpanel.setPeople({
      name,
      [USER_BACKEND_ID.name]: id,
      [DAYS_SINCE_SIGNED_UP.name]: moment
        .utc()
        .diff(moment.utc(created_at * 1000), 'days'),
      [DAYS_SINCE_SUBSCRIBED.name]: payment_account_created_at
        ? moment
            .utc()
            .diff(moment.utc(payment_account_created_at * 1000), 'days')
        : '',
    })

    Mixpanel.register({
      [USER_BACKEND_ID.name]: id,
      [USER_RECURLY_PLAN_NAME.name]: getUserRecurlyPlanName(user),
      [USER_PLAN_NAME.name]: getUserPlanNameOrTier(user, TIER_TO_PLAN_NAME_MAP),
      [USER_PLAN_TIER.name]: getUserPlanNameOrTier(
        user,
        TIER_TO_PLAN_NAME_MAP,
        false,
      ),
      [USER_BILLING_CYCLE.name]: user.subscription?.planInterval ?? null,
      [USER_PRICING_GROUP.name]: getUserPricingGroup(user),
      [USER_STATUS.name]: getUserStatus(user),
      [SIGN_IN_METHOD.name]: getSignInMethod(user),
      [DAYS_SINCE_STARTED_TRIAL.name]: getDaysSinceStartedTrial(user),
      [DAYS_SINCE_SIGNED_UP.name]: getDaysSinceSignedUp(user),
      [START_TRIAL_DATE.name]: getStartTrialDate(user),
      [SIGNED_UP_DATE.name]: getSignedUpDate(user),
      [USER_PARENT.name]: getUserParent(user),
      [USER_DEVICE.name]: getUserDevice(),
    })
  },

  register: (props) => {
    if (REACT_APP_MIXPANEL_API_KEY) {
      mixpanel.register(props)
    }
  },

  startRecording: () => {
    if (isDevEnvironment) return
    mixpanel.start_session_recording()
  },

  stopRecording: () => {
    if (isDevEnvironment) return
    mixpanel.stop_session_recording()
  },

  track: (event, properties = {}, callback = null) => {
    if (event.disabled || !event.enabled) return
    if (!REACT_APP_MIXPANEL_API_KEY) return

    const defaultProps = {}

    if (event.props) {
      // validation for required props
      event.props.forEach(({ prop, required }) => {
        if (required && isNil(properties[prop.name])) {
          return console.error(
            `Mixpanel error: property ${prop.name} is required for event - ${event.name}!`,
          )
        }
      })

      // get default props
      event.props.forEach(({ prop }) => {
        if (prop.defaultValue) {
          defaultProps[prop.name] = prop.defaultValue()
        }
      })
    }

    mixpanel.track(event.name, { ...defaultProps, ...properties }, callback)
  },

  trackAsync: async (event, properties) => {
    await new Promise((resolve, reject) => {
      // Mixpanel can't handle promises for browsers that have Do Not Track enabled
      if (navigator.doNotTrack === '1' && !isDevEnvironment) {
        resolve()
        return
      }
      Mixpanel.track(event, properties, (response) => {
        if (response) {
          resolve()

          return
        }

        reject()
      })
    })
  },

  savePurchaseClickPageName: (purchaseButtonLocation = '') => {
    const pageName = MIXPANEL_PROPS.PAGE_NAME.defaultValue()
    localStorage.setItem(
      'mixpanel_purchase_click_page',
      JSON.stringify({
        pageName,
        action: 'purchase clicked',
        purchaseButtonLocation,
      }),
    )
  },

  saveLastPurchaseButtonLocation: (purchaseButtonLocation) => {
    sessionStorage.setItem(
      'mixpanel_purchase_button_location',
      purchaseButtonLocation,
    )
  },

  getLastPurchaseButtonLocation: () => {
    return sessionStorage.getItem('mixpanel_purchase_button_location')
  },

  saveRegisterClickPageName: (page = null) => {
    const pageName = page || MIXPANEL_PROPS.PAGE_NAME.defaultValue()
    localStorage.setItem(
      'mixpanel_register_click_page',
      JSON.stringify({ pageName, action: 'register clicked' }),
    )
  },

  getPurchaseClickPageName: () => {
    try {
      const info = JSON.parse(
        localStorage.getItem('mixpanel_purchase_click_page'),
      )
      return info.pageName || ''
    } catch {
      return ''
    }
  },

  getRegisterClickPageName: () => {
    try {
      const info = JSON.parse(
        localStorage.getItem('mixpanel_register_click_page'),
      )
      return info.pageName || MIXPANEL_PROPS.PAGE_NAME.defaultValue()
    } catch {
      return MIXPANEL_PROPS.PAGE_NAME.defaultValue()
    }
  },

  sendUserAddonProps: (
    mixpanelEventName,
    {
      user,
      addonType,
      pageName,
      purchaseButtonLocation,
      addonAction: { code = null, event = null } = {},
    },
  ) => {
    const {
      tier,
      subscription: { addOns, planInterval },
    } = user

    let addonTypesInfo = addonType

    const summariseAddons = (arr) => {
      return arr.reduce((accumulator, currentItem) => {
        if (accumulator[currentItem.code]) {
          accumulator[currentItem.code].quantity += currentItem.quantity
        } else {
          accumulator[currentItem.code] = {
            code: currentItem.code,
            quantity: currentItem.quantity,
            project_id: currentItem.project_id,
          }
        }
        return accumulator
      }, {})
    }

    const unitedItems = summariseAddons(addOns)

    if (isArray(addonType)) {
      const addonTypes = summariseAddons(addonType)
      addonTypesInfo = Object.values(addonTypes)
        .filter((itm) => !unitedItems[itm?.code])
        .map(({ addonCode, quantity }) => ({ [addonCode]: quantity }))
    }

    if (code && event) {
      if (code === 'project') {
        // TODO: if need to do sth specefic use this if
        // if (event === 'remove')
      } else if (event === 'add')
        if (unitedItems?.[code]) unitedItems[code].quantity += 1
        else
          unitedItems[code] = {
            code,
            quantity: 1,
          }
      else if (event === 'cancel') {
        if (unitedItems?.[code]) {
          if (unitedItems[code].quantity > 1) unitedItems[code].quantity -= 1
          else delete unitedItems[code]
        }
      }
    }

    const addonInfo = Object.values(unitedItems).reduce(
      (acc, item) => {
        if (item.code.includes('_aiw')) return acc
        acc.addonList.push({
          [item.code]: item.quantity,
        })
        acc.addonCount += item.quantity
        return acc
      },
      {
        addonCount: 0,
        addonList: [],
      },
    )

    const defaultLocation =
      MIXPANEL_PROPS.PAGE_NAME.defaultValue() ||
      Mixpanel.getPurchaseClickPageName()

    Mixpanel.track(mixpanelEventName, {
      [MIXPANEL_PROPS.PAGE_NAME.name]: pageName || defaultLocation,
      [MIXPANEL_PROPS.PLAN_NAME.name]: TIER_TO_PLAN_NAME_MAP[tier],
      [MIXPANEL_PROPS.PLAN_TIER.name]: tier,
      [MIXPANEL_PROPS.BILLING_CYCLE.name]: planInterval,
      [MIXPANEL_PROPS.PAYMENT_METHOD.name]:
        user?.payment_method?.object === 'paypal'
          ? MIXPANEL_PROPS.PAYMENT_METHOD.values.PAYPAL
          : MIXPANEL_PROPS.PAYMENT_METHOD.values.CREDIT_CARD,
      [MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION.name]:
        purchaseButtonLocation || defaultLocation,
      [MIXPANEL_PROPS.ADDON_NAME.name]: addonTypesInfo,
      [MIXPANEL_PROPS.ADDON_COUNT.name]: addonInfo?.addonCount,
      [MIXPANEL_PROPS.ADDON_ON_LIST.name]: addonInfo?.addonList,
      [MIXPANEL_PROPS.USER_HAS_ADD_ONS_ACTIVE.name]: !!(
        addonInfo?.addonCount && addonInfo?.addonCount > 0
      ),
      [MIXPANEL_PROPS.USER_STATUS.name]: MIXPANEL_PROPS.USER_STATUS.values.PAID,
    })
  },

  handlePricingPageView: () => {
    let info = {}
    try {
      info = JSON.parse(localStorage.getItem('mixpanel_purchase_click_page'))
      if (info && info.action === 'purchase clicked') {
        info.action = ''
      } else {
        info = { pageName: MIXPANEL_PROPS.PAGE_NAME.values.PLANS_AND_PRICING }
      }
    } catch {
      info = { pageName: MIXPANEL_PROPS.PAGE_NAME.values.PLANS_AND_PRICING }
    }
    localStorage.setItem('mixpanel_purchase_click_page', JSON.stringify(info))
  },

  getPurchaseButtonLocation: () => {
    try {
      const info = JSON.parse(
        localStorage.getItem('mixpanel_purchase_click_page'),
      )
      return info.purchaseButtonLocation || ''
    } catch {
      return ''
    }
  },

  trackPricingPageView: () => {
    Mixpanel.track(MIXPANEL_EVENTS.PRICING_PAGE_VIEW, {
      [MIXPANEL_PROPS.PAGE_NAME.name]: Mixpanel.getPurchaseClickPageName(),
      [MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION.name]:
        Mixpanel.getLastPurchaseButtonLocation(),
      [MIXPANEL_PROPS.SOURCE.name]: MIXPANEL_PROPS.SOURCE.values.PRICING_PAGE,
    })
  },

  trackCustomPlanClick: () => {
    Mixpanel.track(MIXPANEL_EVENTS.CUSTOM_PLAN_CLICK, {
      [MIXPANEL_PROPS.PAGE_NAME.name]: Mixpanel.getPurchaseClickPageName(),
    })
  },

  trackCustomPlanFormSubmitSuccess: () => {
    Mixpanel.track(MIXPANEL_EVENTS.CUSTOM_PLAN_FORM_SUBMIT_SUCCESS, {
      [MIXPANEL_PROPS.PAGE_NAME.name]: Mixpanel.getPurchaseClickPageName(),
    })
  },

  trackPurchaseClick: (purchaseButtonLocation, buttonCta, source = '') => {
    Mixpanel.savePurchaseClickPageName(purchaseButtonLocation)
    Mixpanel.saveLastPurchaseButtonLocation(purchaseButtonLocation)
    Mixpanel.track(MIXPANEL_EVENTS.PURCHASE_CLICK, {
      [MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION.name]: purchaseButtonLocation,
      [MIXPANEL_PROPS.BUTTON_CTA.name]: buttonCta,
      [MIXPANEL_PROPS.SOURCE.name]:
        source || MIXPANEL_PROPS.SOURCE.values.NEW_CHECKOUT_MODAL,
    })
  },

  trackPlanSelectClick: ({
    tier,
    planInterval,
    buttonLocation = '',
    source = '',
    checkoutModalButtonLocation,
  }) => {
    Mixpanel.track(MIXPANEL_EVENTS.PLAN_SELECT_CLICK, {
      [MIXPANEL_PROPS.PLAN_NAME.name]: TIER_TO_PLAN_NAME_MAP[tier],
      [MIXPANEL_PROPS.PLAN_TIER.name]: tier,
      [MIXPANEL_PROPS.BILLING_CYCLE.name]: planInterval,
      [MIXPANEL_PROPS.PRICING_PAGE_BUTTON_LOCATION.name]: buttonLocation,
      [MIXPANEL_PROPS.PAGE_NAME.name]: Mixpanel.getPurchaseClickPageName(),
      [MIXPANEL_PROPS.SOURCE.name]: source,
      [MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION.name]:
        Mixpanel.getLastPurchaseButtonLocation(),
      [MIXPANEL_PROPS.CHECKOUT_MODAL_BUTTON_LOCATION.name]:
        checkoutModalButtonLocation,
    })
  },

  trackWithUSD: (subscription, params, callback) => {
    const currency = subscription?.currency || 'USD'
    const unitPrice =
      subscription?.discountPrice ||
      subscription?.unitPrice ||
      subscription?.unit_amount?.toString() ||
      '0'
    const amount = parseFloat(unitPrice.replace(/[^\d\.]/g, ''))
    if (currency === 'USD') {
      callback({ ...params, usdAmount: amount })
    } else {
      convertToUSD(amount, currency).then((response) => {
        callback({ ...params, usdAmount: response?.data?.usd_amount || null })
      })
    }
  },

  trackCheckoutViewWrapped: ({ tier, planInterval, usdAmount }) => {
    Mixpanel.track(MIXPANEL_EVENTS.CHECKOUT_VIEW, {
      [MIXPANEL_PROPS.PLAN_NAME.name]: TIER_TO_PLAN_NAME_MAP[tier],
      [MIXPANEL_PROPS.PLAN_TIER.name]: tier,
      [MIXPANEL_PROPS.BILLING_CYCLE.name]: planInterval,
      [MIXPANEL_PROPS.PAGE_NAME.name]: Mixpanel.getPurchaseClickPageName(),
      [MIXPANEL_PROPS.USD_VALUE.name]: usdAmount,
      [MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION.name]:
        Mixpanel.getLastPurchaseButtonLocation(),
      [MIXPANEL_PROPS.SOURCE.name]: MIXPANEL_PROPS.SOURCE.values.PRICING_PAGE,
    })
  },

  trackMetricClick: (metricSelected) => {
    Mixpanel.track(MIXPANEL_EVENTS.METRIC_CLICK, {
      [MIXPANEL_PROPS.METRIC_SELECTED.name]: metricSelected,
    })
  },

  trackViewAllReportClick: (widgetSelected) => {
    Mixpanel.track(MIXPANEL_EVENTS.VIEW_ALL_REPORT_CLICK, {
      [MIXPANEL_PROPS.WIDGET_SELECTED.name]: widgetSelected,
    })
  },

  trackURLExternalRedirectionClick: (widgetSelected, row) => {
    Mixpanel.track(MIXPANEL_EVENTS.URL_EXTERNAL_REDIRECTION_CLICK, {
      [MIXPANEL_PROPS.WIDGET_SELECTED.name]: widgetSelected,
      [MIXPANEL_PROPS.ROW_POSITION.name]: row,
    })
  },

  trackLocationBreakdownSelectClick: (selectedLocation) => {
    Mixpanel.track(MIXPANEL_EVENTS.LOCATION_BREAKDOWN_SELECT_CLICK, {
      [MIXPANEL_PROPS.SELECTED_LOCATION_BREAKDOWN.name]: selectedLocation,
    })
  },

  trackCheckoutView: (
    tier = MIXPANEL_PROPS.PLAN_TIER.values.TIER1,
    planInterval = MIXPANEL_PROPS.BILLING_CYCLE.values.LIFETIME,
    subscription = { currency: 'USD', unitPrice: '$0' },
  ) => {
    Mixpanel.trackWithUSD(
      subscription,
      { tier, planInterval },
      Mixpanel.trackCheckoutViewWrapped,
    )
  },

  trackAiwCheckoutView: (aiwAddon) => {
    Mixpanel.track(MIXPANEL_EVENTS.AIW_CHECKOUT_VIEW, {
      [MIXPANEL_PROPS.AIW_ADDON_TYPE.name]: aiwAddon,
    })
  },

  trackExportSuccess: ({
    exportType,
    chartName = null,
    exportCount,
    exportCsvType,
  }) => {
    Mixpanel.track(MIXPANEL_EVENTS.EXPORT_SUCCESS, {
      [MIXPANEL_PROPS.EXPORT_TYPE.name]: exportType,
      ...(chartName && { [MIXPANEL_PROPS.EXPORTED_CHART.name]: chartName }),
      [MIXPANEL_PROPS.EXPORT_COUNT.name]: exportCount,
      [MIXPANEL_PROPS.EXPORT_CSV_TYPE.name]: exportCsvType,
    })
  },

  trackPaymentSubmitClick: ({
    paymentMethod,
    paymentStatus,
    paymentIssue,
    paymentError,
    subscription,
    tier = MIXPANEL_PROPS.PLAN_TIER.values.TIER1,
    planInterval = MIXPANEL_PROPS.BILLING_CYCLE.values.LIFETIME,
    code = '',
    message = '',
    source = '',
  }) => {
    Mixpanel.track(MIXPANEL_EVENTS.PAYMENT_SUBMIT_CLICK, {
      [MIXPANEL_PROPS.PLAN_NAME.name]: TIER_TO_PLAN_NAME_MAP[tier],
      [MIXPANEL_PROPS.PLAN_TIER.name]: tier,
      [MIXPANEL_PROPS.BILLING_CYCLE.name]: planInterval,
      [MIXPANEL_PROPS.PAYMENT_METHOD.name]: paymentMethod,
      [MIXPANEL_PROPS.PAYMENT_STATUS.name]: paymentStatus,
      [MIXPANEL_PROPS.PAYMENT_ISSUE_DESCRIPTION.name]: paymentIssue,
      [MIXPANEL_PROPS.PAYMENT_ERROR_MESSAGE.name]: paymentError,
      [MIXPANEL_PROPS.SOURCE.name]:
        source || MIXPANEL_PROPS.SOURCE.values.NEW_CHECKOUT_MODAL,
      [MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION.name]:
        Mixpanel.getLastPurchaseButtonLocation(),
    })
    if (paymentStatus !== MIXPANEL_PROPS.PAYMENT_STATUS.values.SUCCESS) {
      const recurlyPaymentErrorMessage =
        code !== CHECKOUT_ERRORS.BLOCKED_PAYMENT_METHOD ? message : ''
      const interfacePaymentErrorMessage =
        code !== CHECKOUT_ERRORS.BLOCKED_PAYMENT_METHOD
          ? message
          : EN_INTERFACE_PAYMENT_ERROR_MESSAGE

      trackHotjarEvent(HOTJAR_EVENTS.CHECKOUT_ERROR)
      Mixpanel.trackCheckoutError({
        tier,
        planInterval,
        paymentMethod,
        subscription,
        paymentErrorParam: code,
        recurlyPaymentErrorMessage,
        interfacePaymentErrorMessage,
        source,
      })
    }
  },

  trackCheckoutSuccessWrapped: ({
    tier,
    planInterval,
    paymentMethod,
    usdAmount,
    source = '',
    purchaseType = '',
  }) => {
    Mixpanel.track(MIXPANEL_EVENTS.CHECKOUT_SUCCESS, {
      [MIXPANEL_PROPS.PLAN_NAME.name]: TIER_TO_PLAN_NAME_MAP[tier],
      [MIXPANEL_PROPS.PLAN_TIER.name]: tier,
      [MIXPANEL_PROPS.BILLING_CYCLE.name]: planInterval,
      [MIXPANEL_PROPS.PAYMENT_METHOD.name]: paymentMethod,
      [MIXPANEL_PROPS.PAGE_NAME.name]: Mixpanel.getPurchaseClickPageName(),
      [MIXPANEL_PROPS.USD_VALUE.name]: usdAmount,
      [MIXPANEL_PROPS.SOURCE.name]: source,
      [MIXPANEL_PROPS.PAYMENT_DATE.name]: moment
        .utc()
        .format('YYYY-MM-DD:HH:MM'),
      [MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION.name]:
        Mixpanel.getLastPurchaseButtonLocation(),
      [MIXPANEL_PROPS.PURCHASE_TYPE.name]: purchaseType,
    })
  },

  trackCheckoutErrorWrapped: ({
    usdAmount,
    tier,
    planInterval,
    paymentMethod,
    recurlyPaymentErrorMessage,
    interfacePaymentErrorMessage,
    paymentErrorParam,
    source,
  }) => {
    Mixpanel.track(MIXPANEL_EVENTS.CHECKOUT_ERROR, {
      [MIXPANEL_PROPS.PLAN_NAME.name]: TIER_TO_PLAN_NAME_MAP[tier],
      [MIXPANEL_PROPS.PLAN_TIER.name]: tier,
      [MIXPANEL_PROPS.BILLING_CYCLE.name]: planInterval,
      [MIXPANEL_PROPS.PAYMENT_METHOD.name]: paymentMethod,
      [MIXPANEL_PROPS.PAGE_NAME.name]: Mixpanel.getPurchaseClickPageName(),
      [MIXPANEL_PROPS.RECURLY_PAYMENT_ERROR_MESSAGE.name]:
        recurlyPaymentErrorMessage,
      [MIXPANEL_PROPS.EN_INTERFACE_PAYMENT_ERROR_MESSAGE.name]:
        interfacePaymentErrorMessage,
      [MIXPANEL_PROPS.PAYMENT_ERROR_PARAM.name]: paymentErrorParam,
      [MIXPANEL_PROPS.USD_VALUE.name]: usdAmount,
      [MIXPANEL_PROPS.SOURCE.name]:
        source || MIXPANEL_PROPS.SOURCE.values.NEW_CHECKOUT_MODAL,
    })
  },

  trackCheckoutSuccess: ({
    tier,
    planInterval,
    paymentMethod,
    subscription,
    source = '',
    purchaseType = '',
  }) => {
    Mixpanel.trackWithUSD(
      subscription,
      { tier, planInterval, paymentMethod, source, purchaseType },
      Mixpanel.trackCheckoutSuccessWrapped,
    )
  },

  trackCheckoutError: ({
    tier,
    planInterval,
    paymentMethod,
    subscription,
    recurlyPaymentErrorMessage,
    interfacePaymentErrorMessage,
    paymentErrorParam,
    source,
  }) => {
    Mixpanel.trackWithUSD(
      subscription,
      {
        tier,
        planInterval,
        paymentMethod,
        recurlyPaymentErrorMessage,
        interfacePaymentErrorMessage,
        paymentErrorParam,
        source,
      },
      Mixpanel.trackCheckoutErrorWrapped,
    )
  },

  trackPayWithPaypalClick: ({ tier, planInterval, source = '' }) => {
    Mixpanel.track(MIXPANEL_EVENTS.PAY_WITH_PAYPAL_CLICK, {
      [MIXPANEL_PROPS.PLAN_NAME.name]: TIER_TO_PLAN_NAME_MAP[tier],
      [MIXPANEL_PROPS.PLAN_TIER.name]: tier,
      [MIXPANEL_PROPS.BILLING_CYCLE.name]: planInterval,
      [MIXPANEL_PROPS.PAGE_NAME.name]: Mixpanel.getPurchaseClickPageName(),
      [MIXPANEL_PROPS.SOURCE.name]: source,
      [MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION.name]:
        Mixpanel.getLastPurchaseButtonLocation(),
    })
  },

  trackProjectCreationClick: (buttonLocation) => {
    let pageName
    switch (buttonLocation) {
      case MIXPANEL_PROPS.ADD_PROJECT_BUTTON_LOCATION.values
        .LANDING_NEW_ACCOUNT:
        pageName = MIXPANEL_PROPS.PAGE_NAME.values.REGISTER
        break
      case MIXPANEL_PROPS.ADD_PROJECT_BUTTON_LOCATION.values
        .LANDING_NEW_PURCHASE:
        pageName = MIXPANEL_PROPS.PAGE_NAME.values.CHECKOUT
        break
      default:
        break
    }
    Mixpanel.track(MIXPANEL_EVENTS.PROJECT_CREATION_CLICK, {
      [MIXPANEL_PROPS.ADD_PROJECT_BUTTON_LOCATION.name]: buttonLocation,
      ...(pageName
        ? {
            [MIXPANEL_PROPS.PAGE_NAME.name]: pageName,
          }
        : {}),
    })
  },

  trackProjectDomainInputSuccess: (buttonLocation) => {
    Mixpanel.track(MIXPANEL_EVENTS.PROJECT_DOMAIN_INPUT_SUCCESS, {
      [MIXPANEL_PROPS.ADD_PROJECT_BUTTON_LOCATION.name]: buttonLocation,
    })
  },

  trackProjectLangLocAddSuccess: ({
    addProjectButtonLocation,
    languages,
    locations,
  }) => {
    Mixpanel.track(MIXPANEL_EVENTS.PROJECT_LANGUAGE_AND_LOCATION_ADD_SUCCESS, {
      [MIXPANEL_PROPS.ADD_PROJECT_BUTTON_LOCATION.name]:
        addProjectButtonLocation,
      [MIXPANEL_PROPS.ADDED_PROJECT_LANGUAGES.name]: languages,
      [MIXPANEL_PROPS.PROJECT_LANGUAGES_COUNT.name]: languages.length,
      [MIXPANEL_PROPS.ADDED_PROJECT_LOCATIONS.name]: locations,
      [MIXPANEL_PROPS.PROJECT_LOCATIONS_COUNT.name]: locations.length,
    })
  },

  trackProjectKeywordsAddSuccess: ({
    addProjectButtonLocation,
    languages,
    locations,
    keywords,
    skippedAddKeywords,
  }) => {
    Mixpanel.track(MIXPANEL_EVENTS.PROJECT_KEYWORDS_ADD_SUCCESS, {
      [MIXPANEL_PROPS.PROJECT_KEYWORDS_COUNT.name]: keywords.length,
      [MIXPANEL_PROPS.SKIPPED_ADD_KEYWORDS.name]: skippedAddKeywords,
      [MIXPANEL_PROPS.ADD_PROJECT_BUTTON_LOCATION.name]:
        addProjectButtonLocation,
      [MIXPANEL_PROPS.ADDED_PROJECT_LANGUAGES.name]: languages,
      [MIXPANEL_PROPS.PROJECT_LANGUAGES_COUNT.name]: languages.length,
      [MIXPANEL_PROPS.ADDED_PROJECT_LOCATIONS.name]: locations,
      [MIXPANEL_PROPS.PROJECT_LOCATIONS_COUNT.name]: locations.length,
    })
  },

  trackProjectCompetitorsAddSuccess: ({
    addProjectButtonLocation,
    languages,
    locations,
    keywords,
    skippedAddKeywords,
    competitors,
    skippedAddCompetitors,
  }) => {
    Mixpanel.track(MIXPANEL_EVENTS.PROJECT_COMPETITORS_ADD_SUCCESS, {
      [MIXPANEL_PROPS.PROJECT_COMPETITORS_COUNT.name]: competitors.length,
      [MIXPANEL_PROPS.SKIPPED_ADD_COMPETITORS.name]: skippedAddCompetitors,
      [MIXPANEL_PROPS.PROJECT_KEYWORDS_COUNT.name]: keywords.length,
      [MIXPANEL_PROPS.SKIPPED_ADD_KEYWORDS.name]: skippedAddKeywords,
      [MIXPANEL_PROPS.ADD_PROJECT_BUTTON_LOCATION.name]:
        addProjectButtonLocation,
      [MIXPANEL_PROPS.ADDED_PROJECT_LANGUAGES.name]: languages,
      [MIXPANEL_PROPS.PROJECT_LANGUAGES_COUNT.name]: languages.length,
      [MIXPANEL_PROPS.ADDED_PROJECT_LOCATIONS.name]: locations,
      [MIXPANEL_PROPS.PROJECT_LOCATIONS_COUNT.name]: locations.length,
    })
  },

  trackProjectCreationSuccess: ({
    addProjectButtonLocation,
    languages,
    locations,
    keywords = [],
    competitors = [],
    skippedAddKeywords,
    skippedAddCompetitors,
    agencyOfferResponse,
  }) => {
    Mixpanel.track(MIXPANEL_EVENTS.PROJECT_CREATION_SUCCESS, {
      [MIXPANEL_PROPS.PROJECT_AGENCY_OFFER_RESPONSE.name]: agencyOfferResponse,
      [MIXPANEL_PROPS.PROJECT_COMPETITORS_COUNT.name]: competitors.length,
      [MIXPANEL_PROPS.SKIPPED_ADD_COMPETITORS.name]: skippedAddCompetitors,
      [MIXPANEL_PROPS.PROJECT_KEYWORDS_COUNT.name]: keywords.length,
      [MIXPANEL_PROPS.SKIPPED_ADD_KEYWORDS.name]: skippedAddKeywords,
      [MIXPANEL_PROPS.ADD_PROJECT_BUTTON_LOCATION.name]:
        addProjectButtonLocation,
      [MIXPANEL_PROPS.ADDED_PROJECT_LANGUAGES.name]: languages ?? [],
      [MIXPANEL_PROPS.PROJECT_LANGUAGES_COUNT.name]: languages?.length ?? 0,
      [MIXPANEL_PROPS.ADDED_PROJECT_LOCATIONS.name]: locations ?? [],
      [MIXPANEL_PROPS.PROJECT_LOCATIONS_COUNT.name]: locations?.length ?? 0,
    })
  },

  trackCreateProjectModalView: () => {
    Mixpanel.track(MIXPANEL_EVENTS.CREATE_PROJECT_MODAL_VIEW)
  },

  trackAiwCheckoutSuccess: (addon, user) => {
    Mixpanel.trackAddonWithUSD(
      addon,
      user,
      Mixpanel.trackAiwCheckoutSuccessWrapped,
    )
  },

  trackAiwCheckoutSuccessWrapped: ({ addonType, usdValue }) => {
    Mixpanel.track(MIXPANEL_EVENTS.AIW_CHECKOUT_SUCCESS, {
      [MIXPANEL_PROPS.AIW_ADDON_TYPE.name]: addonType,
      [MIXPANEL_PROPS.USD_VALUE.name]: usdValue,
    })
  },

  trackAiwOrderBumpView: () => {
    Mixpanel.track(MIXPANEL_EVENTS.AIW_ORDER_BUMP_VIEW, {})
  },

  trackAiwOrderBumpSuccess: (addon, user) => {
    Mixpanel.trackAddonWithUSD(
      addon,
      user,
      Mixpanel.trackAiwOrderBumpSuccessWrapped,
    )
  },

  trackAiwOrderBumpSuccessWrapped: ({ addonType, usdValue }) => {
    Mixpanel.track(MIXPANEL_EVENTS.AIW_ORDER_BUMP_SUCCESS, {
      [MIXPANEL_PROPS.AIW_ADDON_TYPE.name]: addonType,
      [MIXPANEL_PROPS.USD_VALUE.name]: usdValue,
    })
  },

  trackAiwAccountBillingView: () => {
    Mixpanel.track(MIXPANEL_EVENTS.AIW_ACCOUNT_BILLING_VIEW, {})
  },

  trackAddonWithUSD: (addon, user, callback) => {
    const { currency } = getCurrencyAndRegion(user.countryCode)
    const priceSource = addon.addonPrice || addon.price
    const amount = parseFloat(priceSource.replace(/[^\d\.]/g, ''))
    const addonType = addon?.addonType || addon.code.replace('addon_', '')
    if (currency === 'USD') {
      callback({ addonType, usdValue: amount })
    } else {
      convertToUSD(amount, currency).then((response) => {
        callback({ addonType, usdValue: response?.data?.usd_amount || null })
      })
    }
  },

  trackAiwAccountBillingSuccessWrapped: ({ addonType, usdValue }) => {
    Mixpanel.track(MIXPANEL_EVENTS.AIW_ACCOUNT_BILLING_SUCCESS, {
      [MIXPANEL_PROPS.AIW_ADDON_TYPE.name]: addonType,
      [MIXPANEL_PROPS.USD_VALUE.name]: usdValue,
    })
  },

  trackAiwAccountBillingSuccess: (addon, user) => {
    Mixpanel.trackAddonWithUSD(
      addon,
      user,
      Mixpanel.trackAiwAccountBillingSuccessWrapped,
    )
  },

  trackAiwAddonRemoveSuccess: (addon, user) => {
    Mixpanel.trackAddonWithUSD(
      addon,
      user,
      Mixpanel.trackAiwAddonRemoveSuccessWrapped,
    )
  },

  trackAiwAddonRemoveSuccessWrapped: ({ addonType, usdValue }) => {
    Mixpanel.track(MIXPANEL_EVENTS.AIW_ADDON_REMOVE_SUCCESS, {
      [MIXPANEL_PROPS.AIW_ADDON_TYPE.name]: addonType,
      [MIXPANEL_PROPS.USD_VALUE.name]: usdValue,
    })
  },

  trackAtpCrossSellModalClick: () => {
    Mixpanel.track(MIXPANEL_EVENTS.ATP_CROSS_SELL_MODAL_CLICK, {})
  },

  trackSearchSuccess: ({
    currentLocId,
    currentLang,
    searchResultsCount,
    searchInput,
    searchKeywordsInputCount,
    searchInputLength,
    location = '',
    searchInputTerm,
    pageName = '',
  }) => {
    const selectedLanguage = enTranslate(currentLang)
    const selectedLocation = isCity(currentLocId)
      ? location
      : getLocationInfo(currentLocId).canonicalName

    Mixpanel.track(MIXPANEL_EVENTS.SEARCH_SUCCESS, {
      [MIXPANEL_PROPS.SELECTED_LOCATION.name]: selectedLocation,
      [MIXPANEL_PROPS.SELECTED_LANGUAGE.name]: selectedLanguage,
      [MIXPANEL_PROPS.SEARCH_RESULTS_COUNT.name]: searchResultsCount,
      [MIXPANEL_PROPS.SEARCH_INPUT.name]: searchInput,
      [MIXPANEL_PROPS.SEARCH_KEYWORDS_INPUT_COUNT.name]:
        searchKeywordsInputCount,
      [MIXPANEL_PROPS.SEARCH_INPUT_LENGTH.name]: searchInputLength,
      [MIXPANEL_PROPS.SEARCH_INPUT_TERM.name]: searchInputTerm,
      [MIXPANEL_PROPS.PAGE_NAME.name]:
        pageName || MIXPANEL_PROPS.PAGE_NAME.defaultValue(),
    })
  },

  trackLoadedReportView: ({
    contain_results,
    experiment_landing_page_dashboard,
    dashboard_button_clicked,
  } = {}) => {
    const properties = {
      [MIXPANEL_PROPS.EXPERIMENT_LANDING_PAGE_DASHBOARD.name]:
        experiment_landing_page_dashboard,
    }

    if (contain_results) {
      properties[MIXPANEL_PROPS.CONTAIN_RESULTS.name] = contain_results
    }

    if (dashboard_button_clicked) {
      properties[MIXPANEL_PROPS.LANDING_PAGE_DASHBOARD_BUTTON_CLICKED.name] =
        dashboard_button_clicked
    }

    Mixpanel.track(MIXPANEL_EVENTS.LOADED_REPORT_VIEW, properties)
  },

  trackUnloadedReportView: ({
    pageName,
    experiment_landing_page_dashboard,
    dashboard_button_clicked,
  } = {}) => {
    const properties = {
      [MIXPANEL_PROPS.PAGE_NAME.name]:
        pageName || MIXPANEL_PROPS.PAGE_NAME.defaultValue(),
      [MIXPANEL_PROPS.EXPERIMENT_LANDING_PAGE_DASHBOARD.name]:
        experiment_landing_page_dashboard,
    }

    if (dashboard_button_clicked) {
      properties[MIXPANEL_PROPS.LANDING_PAGE_DASHBOARD_BUTTON_CLICKED.name] =
        dashboard_button_clicked
    }

    Mixpanel.track(MIXPANEL_EVENTS.UNLOADED_REPORT_VIEW, properties)
  },

  trackSignInPageView: ({ isAiw, source = '', page = '' }) => {
    const pageName = isAiw ? MIXPANEL_PROPS.PAGE_NAME.values.AIW : page

    Mixpanel.track(MIXPANEL_EVENTS.SIGN_IN_PAGEVIEW, {
      [MIXPANEL_PROPS.PAGE_NAME.name]:
        pageName || MIXPANEL_PROPS.PAGE_NAME.values.LOG_IN,
      [MIXPANEL_PROPS.SOURCE.name]: source,
    })
  },

  trackSignUpPageview: ({ isAiw, source = '', page = '' }) => {
    let pageName
    if (isAiw || page) {
      pageName = isAiw ? 'AIW' : page
    } else {
      let info = {}
      try {
        info = JSON.parse(localStorage.getItem('mixpanel_register_click_page'))
        if (info && info.action === 'register clicked') {
          info.action = ''
        } else {
          info = { pageName: MIXPANEL_PROPS.PAGE_NAME.values.REGISTER }
        }
      } catch {
        info = { pageName: MIXPANEL_PROPS.PAGE_NAME.values.REGISTER }
      }
      localStorage.setItem('mixpanel_register_click_page', JSON.stringify(info))
      pageName = info.pageName
    }

    Mixpanel.track(MIXPANEL_EVENTS.SIGN_UP_PAGEVIEW, {
      [MIXPANEL_PROPS.PAGE_NAME.name]: pageName,
      [MIXPANEL_PROPS.SOURCE.name]: source,
    })
  },

  getPageNameSignUpSignIn: ({ isCheckoutModal, type, page }) => {
    if (page) {
      return page
    }

    if (isCheckoutModal) {
      return MIXPANEL_PROPS.PAGE_NAME.defaultValue()
    }

    const { REGISTER, LOG_IN } = MIXPANEL_PROPS.PAGE_NAME.values
    const defaultValue = type === 'register' ? REGISTER : LOG_IN

    try {
      const info = JSON.parse(
        localStorage.getItem('mixpanel_register_click_page'),
      )
      if (!info || !info.pageName) {
        return defaultValue
      }
      return info.pageName
    } catch {
      return defaultValue
    }
  },

  trackSignUpSuccess: ({
    isAiw = false,
    signInMethod,
    source = '',
    page = '',
    isCheckoutModal,
  }) => {
    const pageName =
      isAiw ||
      Mixpanel.getPageNameSignUpSignIn({
        isCheckoutModal,
        type: 'register',
        page,
      })

    Mixpanel.track(MIXPANEL_EVENTS.SIGN_UP_SUCCESS, {
      [MIXPANEL_PROPS.PAGE_NAME.name]: pageName,
      [MIXPANEL_PROPS.SIGN_IN_METHOD.name]: signInMethod,
      [MIXPANEL_PROPS.SOURCE.name]: source,
    })
  },

  trackSignInSuccess: ({
    signInMethod,
    source = '',
    page = '',
    isCheckoutModal,
    isMonthlyTier1PriceExperimentEnabled,
  }) => {
    const pageName = Mixpanel.getPageNameSignUpSignIn({
      isCheckoutModal,
      type: 'login',
      page,
    })

    Mixpanel.track(MIXPANEL_EVENTS.SIGN_IN_SUCCESS, {
      [MIXPANEL_PROPS.SIGN_IN_METHOD.name]: signInMethod,
      [MIXPANEL_PROPS.SOURCE.name]: source,
      [MIXPANEL_PROPS.PAGE_NAME.name]: pageName,
      [MIXPANEL_PROPS.MONTHLY_TIER_1_PRICE_EXPERIMENT.name]:
        isMonthlyTier1PriceExperimentEnabled,
    })
  },

  trackSiteAuditSuccess: (pagesCrawled) => {
    Mixpanel.track(MIXPANEL_EVENTS.SITE_AUDIT_SUCCESS, {
      [MIXPANEL_PROPS.AUDIT_COUNT_PAGES_CRAWLED.name]: pagesCrawled,
    })
  },

  trackBuyAddonClick: (props) => {
    Mixpanel.sendUserAddonProps(MIXPANEL_EVENTS.BUY_ADDON_CLICK, props)
  },

  trackAddonConfirmationView: (props) => {
    Mixpanel.sendUserAddonProps(MIXPANEL_EVENTS.ADDON_CONFIRMATION_VIEW, props)
  },

  trackBuyAddonSuccess: (props) => {
    Mixpanel.sendUserAddonProps(MIXPANEL_EVENTS.BUY_ADDON_SUCCESS, props)
  },

  trackCancelAddonSuccess: (props) => {
    Mixpanel.sendUserAddonProps(MIXPANEL_EVENTS.CANCEL_ADDON_SUCCSESS, props)
  },

  trackNewCheckoutModalView: () => {
    Mixpanel.track(MIXPANEL_EVENTS.NEW_CHECKOUT_MODAL_VIEW, {
      [MIXPANEL_PROPS.SOURCE.name]:
        MIXPANEL_PROPS.SOURCE.values.NEW_CHECKOUT_MODAL,
      [MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION.name]:
        Mixpanel.getLastPurchaseButtonLocation(),
    })
  },

  trackWantNPDTeamGrowTrafficClick: ({
    addProjectButtonLocation,
    languages,
    locations,
    keywords,
    skippedAddKeywords,
    competitors,
    skippedAddCompetitors,
  }) => {
    Mixpanel.track(MIXPANEL_EVENTS.WANT_NPD_TEAM_GROW_TRAFFIC_CLICK, {
      [MIXPANEL_PROPS.PROJECT_COMPETITORS_COUNT.name]: competitors.length,
      [MIXPANEL_PROPS.SKIPPED_ADD_COMPETITORS.name]: skippedAddCompetitors,
      [MIXPANEL_PROPS.PROJECT_KEYWORDS_COUNT.name]: keywords.length,
      [MIXPANEL_PROPS.SKIPPED_ADD_KEYWORDS.name]: skippedAddKeywords,
      [MIXPANEL_PROPS.ADDED_PROJECT_LANGUAGES.name]: languages,
      [MIXPANEL_PROPS.PROJECT_LANGUAGES_COUNT.name]: languages.length,
      [MIXPANEL_PROPS.ADDED_PROJECT_LOCATIONS.name]: locations,
      [MIXPANEL_PROPS.PROJECT_LOCATIONS_COUNT.name]: locations.length,
      [MIXPANEL_PROPS.ADD_PROJECT_BUTTON_LOCATION.name]:
        addProjectButtonLocation,
    })
  },

  trackNPDConsultingClick: (props = {}) => {
    Mixpanel.track(MIXPANEL_EVENTS.NPD_CONSULTING_CLICK, { ...props })
  },

  trackNPDTeamGrowTrafficLeadSuccess: ({
    addProjectButtonLocation,
    languages,
    locations,
    keywords,
    skippedAddKeywords,
    competitors,
    skippedAddCompetitors,
    businessTypes,
    challenges,
    numberOfEmployees,
    urgency,
    industry,
    budget,
    phone,
    email,
    firstname,
    lastname,
    country,
  }) => {
    Mixpanel.track(MIXPANEL_EVENTS.NPD_TEAM_GROW_TRAFFIC_LEAD_SUCCESS, {
      [MIXPANEL_PROPS.PROJECT_COMPETITORS_COUNT.name]: competitors.length,
      [MIXPANEL_PROPS.SKIPPED_ADD_COMPETITORS.name]: skippedAddCompetitors,
      [MIXPANEL_PROPS.PROJECT_KEYWORDS_COUNT.name]: keywords.length,
      [MIXPANEL_PROPS.SKIPPED_ADD_KEYWORDS.name]: skippedAddKeywords,
      [MIXPANEL_PROPS.ADDED_PROJECT_LANGUAGES.name]: languages,
      [MIXPANEL_PROPS.PROJECT_LANGUAGES_COUNT.name]: languages.length,
      [MIXPANEL_PROPS.ADDED_PROJECT_LOCATIONS.name]: locations,
      [MIXPANEL_PROPS.PROJECT_LOCATIONS_COUNT.name]: locations.length,
      [MIXPANEL_PROPS.ADD_PROJECT_BUTTON_LOCATION.name]:
        addProjectButtonLocation,
      [MIXPANEL_PROPS.BUSINESS_TYPE.name]: businessTypes,
      [MIXPANEL_PROPS.CHALLENGES.name]: challenges,
      [MIXPANEL_PROPS.NUMBER_OF_EMPLOYEES.name]: numberOfEmployees,
      [MIXPANEL_PROPS.URGENCY_FOR_HELP.name]: urgency,
      [MIXPANEL_PROPS.INDUSTRY.name]: industry,
      [MIXPANEL_PROPS.MONTHLY_MARKETING_BUDGET.name]: budget,
      [MIXPANEL_PROPS.PHONE.name]: phone,
      [MIXPANEL_PROPS.EMAIL.name]: email,
      [MIXPANEL_PROPS.FIRSTNAME.name]: firstname,
      [MIXPANEL_PROPS.LASTNAME.name]: lastname,
      [MIXPANEL_PROPS.COUNTRY.name]: country,
    })
  },

  trackExpandDataClick: ({ widgetSelected, displayedMetric, rowPosition }) => {
    Mixpanel.track(MIXPANEL_EVENTS.EXPAND_DATA_CLICK, {
      [MIXPANEL_PROPS.WIDGET_SELECTED.name]: widgetSelected,
      [MIXPANEL_PROPS.DISPLAYED_METRIC.name]: displayedMetric,
      [MIXPANEL_PROPS.ROW_POSITION.name]: rowPosition,
    })
  },

  trackSerpFeaturesToggleClick: ({
    widgetSelected,
    toggleStatus,
    serpFeatures = [],
  }) => {
    const serpFeaturesTranslated = serpFeatures.map((serp) =>
      enTranslate(`snippet_${serp}`),
    )

    Mixpanel.track(MIXPANEL_EVENTS.SERP_FEATURES_TOGGLE_CLICK, {
      [MIXPANEL_PROPS.WIDGET_SELECTED.name]: widgetSelected,
      [MIXPANEL_PROPS.SERP_FEATURES.name]: serpFeaturesTranslated,
      [MIXPANEL_PROPS.TOGGLE_STATUS.name]: toggleStatus,
    })
  },

  trackPaymentSelectClick: ({ tier, planInterval, paymentMethod }) => {
    Mixpanel.track(MIXPANEL_EVENTS.PAYMENT_SELECT_CLICK, {
      [MIXPANEL_PROPS.PLAN_NAME.name]: TIER_TO_PLAN_NAME_MAP[tier],
      [MIXPANEL_PROPS.PLAN_TIER.name]: tier,
      [MIXPANEL_PROPS.BILLING_CYCLE.name]: planInterval,
      [MIXPANEL_PROPS.PAYMENT_METHOD.name]: paymentMethod,
      [MIXPANEL_PROPS.SOURCE.name]:
        MIXPANEL_PROPS.SOURCE.values.NEW_CHECKOUT_MODAL,
      [MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION.name]:
        Mixpanel.getLastPurchaseButtonLocation(),
    })
  },

  trackCancelSubscriptionSuccess: ({
    tier,
    planInterval,
    paymentMethod,
    cancellationFeedback,
    cancellationReason,
  }) => {
    Mixpanel.track(MIXPANEL_EVENTS.CANCEL_SUBSCRIPTION_SUCCESS, {
      [MIXPANEL_PROPS.PLAN_NAME.name]: TIER_TO_PLAN_NAME_MAP[tier],
      [MIXPANEL_PROPS.PLAN_TIER.name]: tier,
      [MIXPANEL_PROPS.BILLING_CYCLE.name]: planInterval,
      [MIXPANEL_PROPS.PAYMENT_METHOD.name]: paymentMethod,
      [MIXPANEL_PROPS.CANCELLATION_FEEDBACK.name]: cancellationFeedback,
      [MIXPANEL_PROPS.CANCELLATION_REASON.name]:
        MIXPANEL_PROPS.CANCELLATION_REASON.values[
          cancellationReason.toUpperCase()
        ],
    })
  },

  trackSendFeebackSuccess: ({
    tier,
    planInterval,
    paymentMethod,
    feedback,
  }) => {
    Mixpanel.track(MIXPANEL_EVENTS.SEND_FEEDBACK_SUCESS, {
      [MIXPANEL_PROPS.PLAN_NAME.name]: TIER_TO_PLAN_NAME_MAP[tier],
      [MIXPANEL_PROPS.PLAN_TIER.name]: tier,
      [MIXPANEL_PROPS.BILLING_CYCLE.name]: planInterval,
      [MIXPANEL_PROPS.PAYMENT_METHOD.name]: paymentMethod,
      [MIXPANEL_PROPS.FEEDBACK.name]: feedback,
    })
  },

  trackSuggestFeatureRedirectionSuccess: () => {
    Mixpanel.track(MIXPANEL_EVENTS.SUGGEST_FEATURE_REDIRECTION_SUCCESS)
  },

  trackTrendSelectionClick: ({ trendSelected }) => {
    Mixpanel.track(MIXPANEL_EVENTS.TREND_SELECTION_CLICK, {
      [MIXPANEL_PROPS.TREND_SELECTED.name]: trendSelected,
    })
  },

  trackTabSelectionClick: ({ tabSelected }) => {
    Mixpanel.track(MIXPANEL_EVENTS.TAB_SELECTION_CLICK, {
      [MIXPANEL_PROPS.TAB_SELECTED.name]: tabSelected,
    })
  },

  /**
   * Tracks the success of adding keywords.
   *
   * @param {Object} params - The parameters for tracking keyword success.
   * @param {number} params.keywordsCount - The number of keywords added.
   * @param {string} params.keywordsType - The type of keywords added.
   * @param {string|string[]} params.currentLang - The current language(s) in use.
   * @param {string|string[]} params.currentLocId - The current location ID(s).
   * @param {number|null} [params.listsCount=null] - The count of lists, if applicable.
   */
  trackAddKeywordSuccess: ({
    keywordsCount,
    keywordsType,
    currentLang,
    currentLocId,
    listsCount = null,
  }) => {
    const selectedLanguage = Array.isArray(currentLang)
      ? currentLang.map((lang) => enTranslate(lang)).join()
      : enTranslate(currentLang)
    const selectedLocation = Array.isArray(currentLocId)
      ? currentLocId.map((locId) => getLocationInfo(locId).canonicalName).join()
      : getLocationInfo(currentLocId).canonicalName

    Mixpanel.track(MIXPANEL_EVENTS.ADD_KEYWORD_SUCCESS, {
      [MIXPANEL_PROPS.SELECTED_LOCATION.name]: selectedLocation,
      [MIXPANEL_PROPS.SELECTED_LANGUAGE.name]: selectedLanguage,
      [MIXPANEL_PROPS.ADDED_KEYWORDS_COUNT.name]: keywordsCount,
      [MIXPANEL_PROPS.ADDED_KEYWORDS_TYPE.name]: keywordsType,
      [MIXPANEL_PROPS.ADDED_LISTS_COUNT.name]: listsCount,
    })
  },

  trackCreateKeywordListSuccess: ({
    keywordCount,
    currentLang,
    currentLocId,
  }) => {
    const selectedLanguage = enTranslate(currentLang)
    const selectedLocation = getLocationInfo(currentLocId).canonicalName

    Mixpanel.track(MIXPANEL_EVENTS.CREATE_KEYWORD_LIST_SUCCESS, {
      [MIXPANEL_PROPS.SELECTED_LOCATION.name]: selectedLocation,
      [MIXPANEL_PROPS.SELECTED_LANGUAGE.name]: selectedLanguage,
      [MIXPANEL_PROPS.KEYWORD_IN_LIST_COUNT.name]: keywordCount,
    })
  },

  trackConnectWithGoogleToolsClick: async ({ buttonLocation }) => {
    localStorage.setItem('mixpanel_google_tools_click_location', buttonLocation)
    await Mixpanel.trackAsync(MIXPANEL_EVENTS.CONNECT_WITH_GOOGLE_TOOLS_CLICK, {
      [MIXPANEL_PROPS.CONNECT_WITH_GOOGLE_BUTTON_LOCATION.name]: buttonLocation,
    })
  },

  trackConnectWithGoogleToolsSuccess: ({ googleToolConnected }) => {
    Mixpanel.track(MIXPANEL_EVENTS.CONNECT_WITH_GOOGLE_TOOLS_SUCCESS, {
      [MIXPANEL_PROPS.CONNECT_WITH_GOOGLE_BUTTON_LOCATION.name]:
        localStorage.getItem('mixpanel_google_tools_click_location'),
      [MIXPANEL_PROPS.GOOGLE_TOOL_CONNECTED.name]: googleToolConnected,
    })
  },
  trackUpdateKeywordClickAndSuccess: ({
    updateMetric,
    updateCount,
    updateType,
    trackType,
  }) => {
    Mixpanel.track(
      trackType === 'click'
        ? MIXPANEL_EVENTS.UPDATE_KEYWORD_CLICK
        : MIXPANEL_EVENTS.UPDATE_KEYWORD_SUCCESS,
      {
        [MIXPANEL_PROPS.UPDATE_METRIC.name]: updateMetric,
        [MIXPANEL_PROPS.UPDATE_KEYWORDS_COUNT.name]: updateCount,
        [MIXPANEL_PROPS.UPDATE_TYPE.name]: updateType,
      },
    )
  },
  trackSeoOpportunitiesPreferenceClick: ({
    preference,
    rowPosition,
    opportunityType,
  }) => {
    Mixpanel.track(MIXPANEL_EVENTS.SEO_OPPORTUNITIES_PREFERENCE_CLICK, {
      [MIXPANEL_PROPS.PREFERENCE.name]: preference,
      [MIXPANEL_PROPS.ROW_POSITION.name]: rowPosition,
      [MIXPANEL_PROPS.OPPORTUNITY_TYPE.name]: opportunityType,
    })
  },
  trackSeoOpportunitiesMarkDoneClick: ({ rowPosition, opportunityType }) => {
    Mixpanel.track(MIXPANEL_EVENTS.SEO_OPPORTUNITIES_MARK_DONE_CLICK, {
      [MIXPANEL_PROPS.ROW_POSITION.name]: rowPosition,
      [MIXPANEL_PROPS.OPPORTUNITY_TYPE.name]: opportunityType,
    })
  },
  trackSeoOpportunitiesStartClick: ({ rowPosition, opportunityType }) => {
    Mixpanel.track(MIXPANEL_EVENTS.SEO_OPPORTUNITIES_START_CLICK, {
      [MIXPANEL_PROPS.ROW_POSITION.name]: rowPosition,
      [MIXPANEL_PROPS.OPPORTUNITY_TYPE.name]: opportunityType,
    })
  },
  trackSearchSuggestionsClick: () => {
    Mixpanel.track(MIXPANEL_EVENTS.SEARCH_SUGGESTIONS_CLICK)
  },
  trackOnboardingQuestionnaireOpen: ({ source }) => {
    Mixpanel.track(
      MIXPANEL_EVENTS.ONBOARDING_QUESTIONNAIRE_EXPERIMENT_PAGE_VIEW,
      {
        source,
      },
    )
  },
  trackOnboardingQuestionnaireComplete: ({ completedLocation }) => {
    Mixpanel.track(MIXPANEL_EVENTS.ONBOARDING_QUESTIONNAIRE_COMPLETE, {
      [MIXPANEL_PROPS.ONBOARDING_QUESTIONNAIRE_COMPLETED_LOCATION.name]:
        completedLocation,
      [MIXPANEL_PROPS.ONBOARDING_QUESTIONNAIRE_NAME.name]:
        MIXPANEL_PROPS.ONBOARDING_QUESTIONNAIRE_NAME.values.ONBOARDING,
    })
  },
  trackPlainFilterClick: ({ filteredMetric, filteredValue }) => {
    Mixpanel.track(MIXPANEL_EVENTS.PLAIN_FILTER_CLICK, {
      [MIXPANEL_PROPS.FILTERED_METRIC.name]: filteredMetric,
      [MIXPANEL_PROPS.FILTERED_VALUE.name]: filteredValue,
    })
  },
  trackSortClick: ({ sortedColumn, sortedType }) => {
    Mixpanel.track(MIXPANEL_EVENTS.SORT_CLICK, {
      [MIXPANEL_PROPS.SORTED_COLUMN.name]: sortedColumn,
      [MIXPANEL_PROPS.SORTED_TYPE.name]: sortedType,
    })
  },
  trackChangePaginationClick: ({ paginationType }) => {
    Mixpanel.track(MIXPANEL_EVENTS.CHANGE_PAGINATION_CLICK, {
      [MIXPANEL_PROPS.PAGINATION_TYPE.name]: paginationType,
    })
  },
  trackPaymentButtonClicked: ({
    tier,
    planInterval,
    buttonName = '',
    source = '',
  }) => {
    Mixpanel.track(MIXPANEL_EVENTS.PAYMENT_BUTTON_CLICKED, {
      [MIXPANEL_PROPS.PLAN_NAME.name]: TIER_TO_PLAN_NAME_MAP[tier],
      [MIXPANEL_PROPS.PLAN_TIER.name]: tier,
      [MIXPANEL_PROPS.BILLING_CYCLE.name]: planInterval,
      [MIXPANEL_PROPS.PAGE_NAME.name]: Mixpanel.getPurchaseClickPageName(),
      [MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION.name]:
        Mixpanel.getLastPurchaseButtonLocation(),
      [MIXPANEL_PROPS.BUTTON_NAME.name]: buttonName,
      [MIXPANEL_PROPS.SOURCE.name]:
        source === 'ubs-pricing-page'
          ? MIXPANEL_PROPS.SOURCE.values.PRICING_PAGE
          : source,
    })
  },
  trackChooseYourPlanView: ({
    source = '',
    planName = '',
    planTier = '',
    billingCycle = '',
    value = '',
  }) => {
    const purchaseButtonLocation = Mixpanel.getPurchaseButtonLocation()
    Mixpanel.track(MIXPANEL_EVENTS.CHOOSE_YOUR_PLAN_VIEW, {
      [MIXPANEL_PROPS.PLAN_NAME.name]: planName,
      [MIXPANEL_PROPS.PLAN_TIER.name]: planTier,
      [MIXPANEL_PROPS.BILLING_CYCLE.name]: billingCycle,
      [MIXPANEL_PROPS.USD_VALUE.name]: value,
      [MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION.name]: purchaseButtonLocation,
      [MIXPANEL_PROPS.SOURCE.name]: source,
    })
  },

  trackCardDetailsView: ({
    planTier = '',
    billingCycle = '',
    subscription = {},
    source = '',
  }) => {
    const purchaseButtonLocation = Mixpanel.getPurchaseButtonLocation()
    Mixpanel.trackWithUSD(
      subscription,
      {
        [MIXPANEL_PROPS.PLAN_NAME.name]: TIER_TO_PLAN_NAME_MAP[planTier],
        [MIXPANEL_PROPS.PLAN_TIER.name]: planTier,
        [MIXPANEL_PROPS.BILLING_CYCLE.name]: billingCycle,
        [MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION.name]: purchaseButtonLocation,
        [MIXPANEL_PROPS.SOURCE.name]: source,
      },
      ({ usdAmount, ...params }) => {
        Mixpanel.track(MIXPANEL_EVENTS.CARD_DETAILS_VIEW, {
          ...params,
          [MIXPANEL_PROPS.USD_VALUE.name]: usdAmount,
        })
      },
    )
  },
  trackCongratulationsView: ({
    planTier = '',
    billingCycle = '',
    subscription = {},
    source = '',
  }) => {
    const purchaseButtonLocation = Mixpanel.getPurchaseButtonLocation()
    Mixpanel.trackWithUSD(
      subscription,
      {
        [MIXPANEL_PROPS.PLAN_NAME.name]: TIER_TO_PLAN_NAME_MAP[planTier],
        [MIXPANEL_PROPS.PLAN_TIER.name]: planTier,
        [MIXPANEL_PROPS.BILLING_CYCLE.name]: billingCycle,
        [MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION.name]: purchaseButtonLocation,
        [MIXPANEL_PROPS.SOURCE.name]: source,
      },
      ({ usdAmount, ...params }) => {
        Mixpanel.track(MIXPANEL_EVENTS.CONGRATULATIONS_VIEW, {
          ...params,
          [MIXPANEL_PROPS.USD_VALUE.name]: usdAmount,
        })
      },
    )
  },
  trackAtpCrossSellModalView: ({
    planTier = '',
    billingCycle = '',
    subscription = {},
    source = '',
  }) => {
    const purchaseButtonLocation = Mixpanel.getPurchaseButtonLocation()
    Mixpanel.trackWithUSD(
      subscription,
      {
        [MIXPANEL_PROPS.PLAN_NAME.name]: TIER_TO_PLAN_NAME_MAP[planTier],
        [MIXPANEL_PROPS.PLAN_TIER.name]: planTier,
        [MIXPANEL_PROPS.BILLING_CYCLE.name]: billingCycle,
        [MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION.name]: purchaseButtonLocation,
        [MIXPANEL_PROPS.SOURCE.name]: source,
      },
      ({ usdAmount, ...params }) => {
        Mixpanel.track(MIXPANEL_EVENTS.ATP_CROSS_SELL_MODAL_VIEW, {
          ...params,
          [MIXPANEL_PROPS.USD_VALUE.name]: usdAmount,
        })
      },
    )
  },

  trackProjectCreationSuccessModalClick: ({ buttonClicked } = {}) => {
    Mixpanel.track(MIXPANEL_EVENTS.PROJECT_CREATION_SUCCESS_MODAL_CLICK, {
      [MIXPANEL_PROPS.BUTTON_CLICKED_PROJECT_CREATION_SUCCESS.name]:
        buttonClicked,
    })
  },

  trackUpdateBillingInformationModalView: () => {
    const user = JSON.parse(localStorage.getItem('user'))
    const { subscription, payment_method } = user

    Mixpanel.track(MIXPANEL_EVENTS.UPDATE_BILLING_INFORMATION_MODAL_VIEW, {
      [MIXPANEL_PROPS.PAGE_NAME.name]:
        MIXPANEL_PROPS.PAGE_NAME.defaultValue() ||
        MIXPANEL_PROPS.PAGE_NAME.values.ACCOUNT_AND_BILLING,
      [MIXPANEL_PROPS.PLAN_NAME.name]: getUserRecurlyPlanName(user),
      [MIXPANEL_PROPS.PLAN_TIER.name]: subscription.tier,
      [MIXPANEL_PROPS.BILLING_CYCLE.name]: subscription.planInterval,
      [MIXPANEL_PROPS.SIGN_IN_METHOD.name]: getSignInMethod(user),
      [MIXPANEL_PROPS.PAYMENT_METHOD.name]:
        payment_method?.object || 'credit_card',
      [MIXPANEL_PROPS.USER_PRICING_GROUP.name]: getUserPricingGroup(user),
      [MIXPANEL_PROPS.USER_PRICING_GROUP.name]: getUserPricingGroup(user),
      [MIXPANEL_PROPS.USER_CURRENT_PLAN.name]: getUserRecurlyPlanName(user),
      [MIXPANEL_PROPS.SOURCE.name]: MIXPANEL_PROPS.SOURCE.values.BILLING_PAGE,
    })
  },

  trackUpdateBillingInformationError: ({
    errorName,
    errorDescription,
    paymentMethod,
  }) => {
    const user = JSON.parse(localStorage.getItem('user'))
    const { subscription } = user

    Mixpanel.track(MIXPANEL_EVENTS.UPDATE_BILLING_INFORMATION_ERROR, {
      [MIXPANEL_PROPS.PAGE_NAME.name]:
        MIXPANEL_PROPS.PAGE_NAME.defaultValue() ||
        MIXPANEL_PROPS.PAGE_NAME.values.ACCOUNT_AND_BILLING,
      [MIXPANEL_PROPS.PLAN_NAME.name]: getUserRecurlyPlanName(user),
      [MIXPANEL_PROPS.PLAN_TIER.name]: subscription.tier,
      [MIXPANEL_PROPS.BILLING_CYCLE.name]: subscription.planInterval,
      [MIXPANEL_PROPS.SIGN_IN_METHOD.name]: getSignInMethod(user),
      [MIXPANEL_PROPS.PAYMENT_METHOD.name]: paymentMethod || 'credit_card',
      [MIXPANEL_PROPS.USER_PRICING_GROUP.name]: getUserPricingGroup(user),
      [MIXPANEL_PROPS.USER_PRICING_GROUP.name]: getUserPricingGroup(user),

      [MIXPANEL_PROPS.PAYMENT_ERROR_MESSAGE.name]: errorName,
      [MIXPANEL_PROPS.PAYMENT_ISSUE_DESCRIPTION.name]: errorDescription,
      [MIXPANEL_PROPS.SOURCE.name]: MIXPANEL_PROPS.SOURCE.values.BILLING_PAGE,
      [MIXPANEL_PROPS.BUTTON_NAME.name]: paymentMethod || '',
    })
  },

  trackUpdateBillingInformationUpdate: ({ paymentMethod }) => {
    const user = JSON.parse(localStorage.getItem('user'))
    const { subscription, payment_method } = user

    Mixpanel.track(MIXPANEL_EVENTS.UPDATE_BILLING_INFORMATION_UPDATED, {
      [MIXPANEL_PROPS.PAGE_NAME.name]:
        MIXPANEL_PROPS.PAGE_NAME.defaultValue() ||
        MIXPANEL_PROPS.PAGE_NAME.values.ACCOUNT_AND_BILLING,
      [MIXPANEL_PROPS.PLAN_NAME.name]: getUserRecurlyPlanName(user),
      [MIXPANEL_PROPS.PLAN_TIER.name]: subscription.tier,
      [MIXPANEL_PROPS.BILLING_CYCLE.name]: subscription.planInterval,
      [MIXPANEL_PROPS.SIGN_IN_METHOD.name]: getSignInMethod(user),
      [MIXPANEL_PROPS.PAYMENT_METHOD.name]:
        payment_method?.object || 'credit_card',
      [MIXPANEL_PROPS.USER_PRICING_GROUP.name]: getUserPricingGroup(user),
      [MIXPANEL_PROPS.USER_PRICING_GROUP.name]: getUserPricingGroup(user),

      [MIXPANEL_PROPS.SOURCE.name]: MIXPANEL_PROPS.SOURCE.values.BILLING_PAGE,

      [MIXPANEL_PROPS.PAYMENT_METHOD.name]: paymentMethod || 'credit_card',
      [MIXPANEL_PROPS.USER_CURRENT_PLAN.name]: getUserRecurlyPlanName(user),
      [MIXPANEL_PROPS.BUTTON_NAME.name]: paymentMethod || '',
    })
  },

  trackCopySuccess: ({ copyCount, copyType }) => {
    Mixpanel.track(MIXPANEL_EVENTS.COPY_SUCCESS, {
      [MIXPANEL_PROPS.COPY_COUNT.name]: copyCount,
      [MIXPANEL_PROPS.COPY_TYPE.name]: copyType,
    })
  },

  trackChangeEmail: (event) => {
    const user = JSON.parse(localStorage.getItem('user'))
    const { subscription, payment_method } = user

    Mixpanel.track(event, {
      [MIXPANEL_PROPS.PLAN_NAME.name]: getUserRecurlyPlanName(user),
      [MIXPANEL_PROPS.PLAN_TIER.name]: subscription?.tier,
      [MIXPANEL_PROPS.BILLING_CYCLE.name]: subscription?.planInterval,
      [MIXPANEL_PROPS.SIGN_IN_METHOD.name]: getSignInMethod(user),
      [MIXPANEL_PROPS.PAYMENT_METHOD.name]: payment_method?.object,
      [MIXPANEL_PROPS.USER_CURRENT_PLAN.name]: getUserRecurlyPlanName(user),
      [MIXPANEL_PROPS.USER_PRICING_GROUP.name]: getUserPricingGroup(user),
      [MIXPANEL_PROPS.SOURCE.name]: MIXPANEL_PROPS.SOURCE.values.BILLING_PAGE,
      [MIXPANEL_PROPS.USER_STATUS.name]: getUserStatus(user),
    })
  },

  trackChooseYourSuccessPathView: ({
    addProjectButtonLocation,
    languages,
    locations,
    keywords,
    skippedAddKeywords,
    competitors,
    skippedAddCompetitors,
  }) => {
    Mixpanel.track(MIXPANEL_EVENTS.CHOOSE_YOUR_SUCCESS_PATH_VIEW, {
      [MIXPANEL_PROPS.PROJECT_COMPETITORS_COUNT.name]: competitors.length,
      [MIXPANEL_PROPS.SKIPPED_ADD_COMPETITORS.name]: skippedAddCompetitors,
      [MIXPANEL_PROPS.PROJECT_KEYWORDS_COUNT.name]: keywords.length,
      [MIXPANEL_PROPS.SKIPPED_ADD_KEYWORDS.name]: skippedAddKeywords,
      [MIXPANEL_PROPS.ADDED_PROJECT_LANGUAGES.name]: languages,
      [MIXPANEL_PROPS.PROJECT_LANGUAGES_COUNT.name]: languages.length,
      [MIXPANEL_PROPS.ADDED_PROJECT_LOCATIONS.name]: locations,
      [MIXPANEL_PROPS.PROJECT_LOCATIONS_COUNT.name]: locations.length,
      [MIXPANEL_PROPS.ADD_PROJECT_BUTTON_LOCATION.name]:
        addProjectButtonLocation,
    })
  },

  trackChooseYourSuccessPathCTAClicked: ({
    addProjectButtonLocation,
    languages,
    locations,
    keywords,
    skippedAddKeywords,
    competitors,
    skippedAddCompetitors,
    onboardingSuccessPath,
  }) => {
    Mixpanel.track(MIXPANEL_EVENTS.CHOOSE_YOUR_SUCCESS_PATH_CTA_CLICKED, {
      [MIXPANEL_PROPS.PROJECT_COMPETITORS_COUNT.name]: competitors.length,
      [MIXPANEL_PROPS.SKIPPED_ADD_COMPETITORS.name]: skippedAddCompetitors,
      [MIXPANEL_PROPS.PROJECT_KEYWORDS_COUNT.name]: keywords.length,
      [MIXPANEL_PROPS.SKIPPED_ADD_KEYWORDS.name]: skippedAddKeywords,
      [MIXPANEL_PROPS.ADDED_PROJECT_LANGUAGES.name]: languages,
      [MIXPANEL_PROPS.PROJECT_LANGUAGES_COUNT.name]: languages.length,
      [MIXPANEL_PROPS.ADDED_PROJECT_LOCATIONS.name]: locations,
      [MIXPANEL_PROPS.PROJECT_LOCATIONS_COUNT.name]: locations.length,
      [MIXPANEL_PROPS.ADD_PROJECT_BUTTON_LOCATION.name]:
        addProjectButtonLocation,
      [MIXPANEL_PROPS.ONBOARDING_SUCCESS_PATH.name]: onboardingSuccessPath,
    })
  },
  trackUploadCSVSuccess: ({ uploadCount }) => {
    Mixpanel.track(MIXPANEL_EVENTS.UPLOAD_CSV_SUCCESS, {
      [MIXPANEL_PROPS.UPLOAD_COUNT.name]: uploadCount,
    })
  },
}
